import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

/* ─── Validación de cédula/RUC ecuatoriano (mismo algoritmo que el
   backend) — aviso inmediato antes de guardar, para casos como la #9122
   (RUC de 13 dígitos guardado como "Cédula") o la #9119 (dígito
   faltante). El backend valida lo mismo como respaldo final. ─── */
function validarCedulaEC(cedula) {
  if (!/^\d{10}$/.test(cedula)) return false;
  const digitos = cedula.split('').map(Number);
  const provincia = parseInt(cedula.substring(0, 2), 10);
  if (provincia < 1 || provincia > 24) return false;
  if (digitos[2] > 6) return false;
  const coeficientes = [2,1,2,1,2,1,2,1,2];
  let suma = 0;
  for (let i = 0; i < 9; i++) {
    let val = digitos[i] * coeficientes[i];
    if (val > 9) val -= 9;
    suma += val;
  }
  const verificador = (10 - (suma % 10)) % 10;
  return verificador === digitos[9];
}
function validarIdentificacion(tipo, identificacion) {
  const id = (identificacion || '').trim();
  if (tipo === 'CDL') {
    if (!/^\d{10}$/.test(id)) return 'La cédula debe tener exactamente 10 dígitos';
    if (!validarCedulaEC(id)) return 'El número de cédula no es válido (dígito verificador incorrecto) — revisa que esté bien digitado';
    return null;
  }
  if (tipo === 'RUC') {
    if (!/^\d{13}$/.test(id)) return 'El RUC debe tener exactamente 13 dígitos';
    if (id.substring(10) === '000') return 'El RUC no es válido (el establecimiento no puede ser "000")';
    const tercerDigito = parseInt(id[2], 10);
    if (tercerDigito <= 5 && !validarCedulaEC(id.substring(0, 10))) {
      return 'El RUC no es válido (dígito verificador incorrecto) — revisa que esté bien digitado';
    }
    return null;
  }
  return null; // PSP y NAP: sin validación de formato
}
// Si el usuario escribe un número de un largo que no corresponde al tipo
// seleccionado (10 dígitos = cédula, 13 = RUC), sugiere el tipo correcto
// en vez de dejarlo pasar en silencio — esto es justo lo que faltó y
// causó el rechazo del SRI en la factura #9122.
function tipoSugeridoPorLargo(identificacion) {
  const id = (identificacion || '').trim();
  if (/^\d{10}$/.test(id)) return 'CDL';
  if (/^\d{13}$/.test(id)) return 'RUC';
  return null;
}

const css = `
.fac-wrap{display:grid;grid-template-columns:1fr 400px;gap:14px;height:calc(100vh - 130px);}
.fac-lista{display:flex;flex-direction:column;background:var(--surface);border-radius:10px;border:1px solid var(--border);overflow:hidden;}
.fac-panel{background:var(--surface);border-radius:10px;border:1px solid var(--border);overflow:hidden;display:flex;flex-direction:column;}
.fac-hdr{padding:10px 16px;background:var(--navy);color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:space-between;}
.fac-filters{padding:10px 14px;border-bottom:1px solid var(--border);display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;}
.fac-body{flex:1;overflow-y:auto;}
.fac-row{padding:9px 14px;border-bottom:1px solid var(--bg);cursor:pointer;display:grid;grid-template-columns:1fr auto;gap:3px;transition:background .1s;}
.fac-row:hover{background:var(--blue-lt);}
.fac-row.sel{background:var(--blue-lt);border-left:3px solid var(--navy);}
.fac-det-body{flex:1;overflow-y:auto;padding:14px;}
.fac-tbl{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:10px;}
.fac-tbl th{background:#1a2535;color:#fff;padding:6px 8px;font-weight:600;text-align:left;}
.fac-tbl td{padding:5px 7px;border-bottom:1px solid var(--border);}
.fac-total{background:var(--navy);color:#fff;border-radius:8px;padding:10px 14px;display:grid;grid-template-columns:1fr auto;gap:3px;font-size:12px;}
.tab-btns{display:flex;border-bottom:1px solid var(--border);}
.tab-btn{padding:8px 16px;font-size:12px;cursor:pointer;border:none;background:transparent;border-bottom:2px solid transparent;font-weight:500;color:var(--text2);}
.tab-btn.on{border-bottom-color:var(--navy);color:var(--navy);font-weight:700;}
.mb{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:200;display:flex;align-items:center;justify-content:center;padding:16px;}
.mx{background:#fff;border-radius:10px;width:100%;max-width:680px;max-height:93vh;overflow-y:auto;box-shadow:0 10px 40px rgba(0,0,0,.25);}
.mh{padding:12px 18px;border-radius:10px 10px 0 0;display:flex;align-items:center;justify-content:space-between;}
.mbd{padding:16px;}
.mf{padding:10px 16px;border-top:1px solid var(--border);display:flex;gap:8px;justify-content:flex-end;}
.inp{width:100%;padding:5px 7px;font-size:12px;border:1px solid var(--border);border-radius:4px;}
.mx-fac{max-width:1080px;}
.fac-grid{display:grid;grid-template-columns:1fr 320px;gap:20px;align-items:start;margin-top:14px;}
.fac-main{min-width:0;}
.fac-side{min-width:0;}
.side-card{background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:14px;margin-bottom:12px;}
.side-card h4{margin:0 0 10px;font-size:11px;text-transform:uppercase;letter-spacing:.4px;color:var(--text3);font-weight:700;}
.sum-row2{display:flex;justify-content:space-between;font-size:12px;color:var(--text2);padding:5px 0;border-bottom:1px solid var(--border);}
.sum-row2.total{border-bottom:none;border-top:2px solid var(--navy-lt);margin-top:6px;padding-top:10px;font-size:16px;font-weight:700;color:var(--navy);}
.items-tbl{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:12px;}
.items-tbl th{background:var(--navy);color:#fff;padding:9px 10px;font-weight:600;text-align:left;font-size:10.5px;text-transform:uppercase;letter-spacing:.3px;}
.items-tbl td{padding:10px;border-bottom:1px solid var(--border);vertical-align:top;}
.items-tbl tbody tr:nth-child(even){background:var(--surface2);}
.stock-afectado-row{display:flex;justify-content:space-between;font-size:11.5px;padding:5px 0;border-bottom:1px dashed var(--border);}
.stock-afectado-row:last-child{border-bottom:none;}
.stock-afectado-row .v{font-weight:700;}
.stock-afectado-row .v.warn{color:var(--red);}
.stock-afectado-row .v.ok{color:var(--green);}
.pago-tbl-mini{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:8px;}
.pago-tbl-mini td{padding:4px 3px;}
.buscador-item{position:relative;margin-bottom:6px;}
.buscador-item input{width:100%;padding:8px 10px 8px 30px;font-size:12px;border:1.5px solid var(--navy-lt);border-radius:6px;}
.buscador-item .ic-search{position:absolute;left:9px;top:50%;transform:translateY(-50%);font-size:13px;pointer-events:none;}
.buscador-drop{position:absolute;top:100%;left:0;right:0;background:#fff;border:1px solid var(--border);border-radius:0 0 6px 6px;z-index:20;max-height:260px;overflow-y:auto;box-shadow:0 6px 18px rgba(0,0,0,.12);}
.buscador-row{display:flex;align-items:center;gap:8px;padding:7px 10px;cursor:pointer;border-bottom:1px solid var(--bg);}
.buscador-row:hover{background:var(--blue-lt);}
.buscador-row .bi{width:24px;height:24px;border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:12px;flex-shrink:0;}
.buscador-row .bi.serv{background:var(--blue-lt);color:var(--navy);}
.buscador-row .bi.med{background:var(--green-lt);color:var(--green);}
.buscador-row .bn{flex:1;min-width:0;font-size:12px;font-weight:600;color:var(--text);}
.buscador-row .bm{font-size:10px;color:var(--text3);}
.buscador-row .bp{text-align:right;font-size:12px;font-weight:700;color:var(--navy);white-space:nowrap;}
.buscador-row .bs{font-size:9.5px;font-weight:700;margin-top:1px;}
.buscador-row .bs.ok{color:var(--green);}
.buscador-row .bs.low{color:var(--amber);}
.tipo-badge{display:inline-block;font-size:8.5px;font-weight:700;padding:1px 5px;border-radius:8px;text-transform:uppercase;letter-spacing:.2px;white-space:nowrap;}
.tipo-badge.serv{background:var(--blue-lt);color:var(--navy-lt);}
.tipo-badge.med{background:var(--green-lt);color:var(--green);}
.stock-nota{font-size:9.5px;margin-top:2px;font-weight:600;}
.stock-nota.ok{color:var(--green);}
.stock-nota.warn{color:var(--red);}

/* ── Módulo de Facturación: página completa (sin modal) ── */
.fmod-tabs{display:flex;gap:4px;border-bottom:1px solid var(--border);margin-bottom:16px;overflow-x:auto;}
.fmod-tab{padding:11px 18px;font-size:13.5px;font-weight:600;color:var(--text3);cursor:pointer;white-space:nowrap;
  border-bottom:3px solid transparent;background:none;border-top:none;border-left:none;border-right:none;}
.fmod-tab.on{color:var(--navy);border-bottom-color:var(--navy);}
.fpage-header{background:var(--navy);color:#fff;padding:14px 20px;border-radius:10px 10px 0 0;display:flex;align-items:center;justify-content:space-between;gap:12px;}
.fpage-header .crumb{font-size:11.5px;color:#b9c4de;margin-bottom:3px;}
.fpage-header h2{margin:0;font-size:16px;font-weight:700;}
.fpage-back{width:30px;height:30px;border-radius:8px;background:rgba(255,255,255,.14);display:flex;align-items:center;justify-content:center;font-size:15px;cursor:pointer;flex-shrink:0;border:none;color:#fff;}
.fpage-body{background:#fff;border:1px solid var(--border);border-top:none;border-radius:0 0 10px 10px;padding:20px;}

.kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:18px;}
.kpi-card{background:#fff;border:1px solid var(--border);border-radius:10px;padding:15px 17px;}
.kpi-card .lbl{font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;color:var(--text3);margin-bottom:7px;}
.kpi-card .num{font-size:22px;font-weight:700;color:var(--navy);}
.kpi-card .tag{display:inline-block;margin-top:6px;font-size:10.5px;font-weight:600;padding:2px 8px;border-radius:9px;}
.kpi-card .tag.g{background:var(--green-lt);color:var(--green);}
.kpi-card .tag.b{background:var(--blue-lt);color:var(--navy-lt);}
.kpi-card .tag.a{background:var(--amber-lt);color:var(--amber);}
.kpi-card .tag.r{background:var(--red-lt);color:var(--red);}

.flt-card{background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:16px 18px;margin-bottom:16px;}
.flt-card h4{margin:0 0 12px;font-size:12.5px;text-transform:uppercase;letter-spacing:.3px;color:var(--text3);font-weight:700;display:flex;justify-content:space-between;align-items:center;}
.flt-row{display:flex;gap:12px;flex-wrap:wrap;align-items:flex-end;}
.flt-g{flex:1;min-width:200px;position:relative;}
.flt-g.sm{flex:0 0 140px;min-width:140px;}
.flt-g label{display:block;font-size:10px;font-weight:700;text-transform:uppercase;color:var(--text3);margin-bottom:5px;}
.flt-g input,.flt-g select{width:100%;padding:8px 10px;font-size:12.5px;border:1px solid var(--border2);border-radius:6px;background:#fff;color:var(--text);}
.flt-auto{position:absolute;top:100%;left:0;right:0;background:#fff;border:1px solid var(--border2);border-radius:0 0 7px 7px;z-index:15;box-shadow:0 8px 18px rgba(20,33,61,.14);max-height:180px;overflow-y:auto;}
.flt-auto-item{padding:8px 12px;font-size:12px;cursor:pointer;border-bottom:1px solid var(--border);}
.flt-auto-item:hover{background:var(--blue-lt);}
.flt-auto-item:last-child{border-bottom:none;}
.flt-auto-item b{color:var(--text);}
.flt-auto-item span{color:var(--text3);margin-left:6px;font-size:11px;}

table.lst-tbl{width:100%;border-collapse:collapse;font-size:12.5px;}
table.lst-tbl thead th{text-align:left;font-size:10.5px;text-transform:uppercase;letter-spacing:.3px;color:var(--text3);padding:8px 9px;border-bottom:2px solid var(--border2);font-weight:700;}
table.lst-tbl thead th.num{text-align:right;}
table.lst-tbl tbody td{padding:11px 9px;border-bottom:1px solid var(--border);}
table.lst-tbl tbody td.num{text-align:right;font-variant-numeric:tabular-nums;font-weight:600;}
table.lst-tbl tbody tr:hover{background:var(--surface2);cursor:pointer;}
.pill2{display:inline-block;font-size:10px;font-weight:700;padding:3px 9px;border-radius:9px;}
.pill2.activa{background:var(--green-lt);color:var(--green);}
.pill2.anulada{background:var(--border);color:var(--text3);text-decoration:line-through;}
.pill2.sri-a{background:var(--green-lt);color:var(--green);}
.pill2.sri-f{background:var(--amber-lt);color:var(--amber);}
.pill2.sri-err{background:var(--red-lt);color:var(--red);}
.lnk2{color:var(--navy-lt);font-weight:600;font-size:11.5px;margin-right:9px;cursor:pointer;}
.tbl-foot2{display:flex;justify-content:space-between;align-items:center;margin-top:12px;font-size:11.5px;color:var(--text3);}
.pager2{display:flex;gap:4px;}
.pg2{min-width:26px;height:26px;padding:0 6px;border-radius:6px;border:1px solid var(--border2);display:flex;align-items:center;justify-content:center;font-size:11.5px;color:var(--text2);background:#fff;cursor:pointer;}
.pg2.cur{background:var(--navy);color:#fff;border-color:var(--navy);}
.pg2:disabled{opacity:.4;cursor:default;}

/* Cierre de caja */
.resumen-grid2{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:18px;}
.resumen-card2{background:#fff;border:1px solid var(--border);border-radius:10px;padding:15px 17px;}
.resumen-card2 .lbl{font-size:10.5px;font-weight:700;text-transform:uppercase;color:var(--text3);margin-bottom:7px;}
.resumen-card2 .num{font-size:19px;font-weight:700;color:var(--navy);}
.resumen-card2 .num.ok{color:var(--green);}
.resumen-card2 .num.bad{color:var(--red);}
table.formas-tbl2{width:100%;border-collapse:collapse;font-size:12.5px;margin-bottom:6px;}
table.formas-tbl2 th{text-align:left;font-size:10.5px;text-transform:uppercase;color:var(--text3);padding:7px 9px;border-bottom:2px solid var(--border2);}
table.formas-tbl2 th.num{text-align:right;}
table.formas-tbl2 td{padding:9px;border-bottom:1px solid var(--border);}
table.formas-tbl2 td.num{text-align:right;font-weight:700;}
.diff-banner2{border-radius:8px;padding:11px 14px;font-size:12.5px;margin:12px 0;display:flex;gap:9px;align-items:flex-start;}
.diff-banner2.ok{background:var(--green-lt);border:1px solid #b9e3cb;color:#0f5c33;}
.diff-banner2.bad{background:var(--red-lt);border:1px solid #f0c3c3;color:#7a2020;}
table.hist-tbl2{width:100%;border-collapse:collapse;font-size:12px;}
table.hist-tbl2 th{text-align:left;font-size:10px;text-transform:uppercase;color:var(--text3);padding:7px 9px;border-bottom:2px solid var(--border2);}
table.hist-tbl2 td{padding:8px 9px;border-bottom:1px solid var(--border);}

/* ── Responsive: celular / pantallas angostas ── */
@media (max-width:760px){
  .fpage-header{padding:11px 14px;}
  .fpage-header h2{font-size:14.5px;}
  .fpage-body{padding:13px;}
  .fmod-tab{padding:9px 12px;font-size:12.5px;}

  .fac-grid{grid-template-columns:1fr;gap:14px;}
  .fac-side{order:2;}
  .fac-main{order:1;}

  .kpi-grid{grid-template-columns:repeat(2,1fr);gap:10px;}
  .kpi-card{padding:12px 13px;}
  .kpi-card .num{font-size:19px;}

  .resumen-grid2{grid-template-columns:repeat(2,1fr);gap:10px;}
  .resumen-card2{padding:12px 13px;}
  .resumen-card2 .num{font-size:16.5px;}

  .flt-row{flex-direction:column;align-items:stretch;gap:10px;}
  .flt-g,.flt-g.sm{min-width:0;width:100%;flex:none;}

  table.lst-tbl,table.formas-tbl2,table.hist-tbl2{font-size:11.5px;}
  .tbl-foot2{flex-direction:column;align-items:flex-start;gap:8px;}
}
`;

const ESTADOS = {
  ACT:{ label:'Activa',   bg:'#5BAD8F', fg:'#fff' },
  CNC:{ label:'Cancelada',bg:'#6C9BD2', fg:'#fff' },
  ANL:{ label:'Anulada',  bg:'#EF9A9A', fg:'#333' },
};

// Estado SRI de la factura -- distinto del estado de negocio (ACT/ANL) de
// arriba. 'A' es lo único realmente "Autorizada"; F/D/E se agrupan como
// "No autorizada" mostrando el motivo real (recién creada sin enviar,
// devuelta por el SRI, o error), así se ve de un vistazo si hace falta
// reenviarla o si es normal que aún no tenga autorización.
const ESTADOS_SRI = {
  A: { label:'Autorizada',    clase:'sri-a'   },
  F: { label:'No autorizada · sin enviar', clase:'sri-f'   },
  D: { label:'No autorizada · devuelta',   clase:'sri-err' },
  E: { label:'No autorizada · error',      clase:'sri-err' },
};

const fmt = v => `$${Number(v||0).toFixed(2)}`;

// Icono según el nombre de la forma de pago (catálogo real: EFECTIVO,
// TARJETA DE CRÉDITO, DUNA, ROL DE PAGOS, TRANSFERENCIA, CHEQUE, ...)
const iconoFormaPago = nombre => {
  const n = (nombre||'').toUpperCase();
  if (n.includes('EFECTIVO'))        return '💵';
  if (n.includes('TARJETA'))         return '💳';
  if (n.includes('TRANSFEREN'))      return '🏦';
  if (n.includes('DUNA'))            return '🏧';
  if (n.includes('ROL DE PAGOS'))    return '🧾';
  if (n.includes('CHEQUE'))          return '📝';
  return '💰';
};

/* ── Modal Factura ──────────────────────────────────── */
function ModalFactura({ turno, cajas, formasPago, servicios, ivaGlobal=15, onGuardar, onCerrar }) {
  const [tab, setTab] = useState('productos');
  const [historial, setHistorial] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);
  const [histErr, setHistErr] = useState('');
  const [form, setForm] = useState({
    COD_CAJA:              cajas[0]?.COD || '',
    COD_PACIENTE:          turno?.COD_PACIENTE || '',
    C_TIPO_IDENTIFICACION: turno?.C_TIPO_IDENTIFICACION || 'CDL',
    IDENTIFICACION:        turno?.IDENTIFICACION || '',
    RAZON_SOCIAL:          turno ? `${turno.APELLIDO_PATERNO||''} ${turno.APELLIDO_MATERNO||''} ${turno.PRIMER_NOMBRE||''}`.trim() : '',
    DIRECCION:             turno?.DIRECCION || '',
    TELEFONO:              turno?.TELEFONO || '',
    EMAIL:                 turno?.EMAIL || '',
    OBSERVACION:           turno ? `TURNO #${turno.NUMERO ?? '?'}${turno.MEDICO ? ' - ' + turno.MEDICO : ''}` : '',
    MEDICO_FACTURA:        null, // se resuelve abajo contra el backend (¿el médico de este turno tiene RUC propio?); null = Centro
  });
  const [detalles, setDetalles] = useState(
    turno ? [{
      DESCRIPCION:    turno.SERVICIO,
      CANTIDAD:       1,
      VALOR:          Number(turno.VALOR) || 0,
      DESCUENTO:      0,
      PORCENTAJE_IVA: turno.APLICA_IVA ? Number(turno.IVA_GLOBAL||turno.PORCENTAJE_IVA||0) : 0,
      COD_CONSUMO:    null,
    }] : []
  );
  const [pagos,   setPagos]   = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');
  const [sriInfo, setSriInfo] = useState(null);
  const [pendientes,  setPendientes]  = useState([]);
  const [pendSel,     setPendSel]     = useState([]);
  const [consumosIds, setConsumosIds] = useState([]);
  const [cajaAbierta, setCajaAbierta] = useState(null); // null=cargando, true/false

  useEffect(() => {
    if (!form.COD_CAJA) { setCajaAbierta(null); return; }
    setCajaAbierta(null);
    api.get(`/facturacion/cierre-caja/estado?cod_caja=${form.COD_CAJA}`)
      .then(r => setCajaAbierta(!!r.data?.abierto))
      .catch(() => setCajaAbierta(null));
  }, [form.COD_CAJA]);

  // Si la factura se abrió directo desde un turno, ¿el médico de ese turno
  // factura con su propio RUC? Antes esto comparaba el usuario contra un
  // nombre fijo ('GVALDIVIESO') — solo funcionaba para ese médico puntual.
  // Ahora se pregunta al backend (misma fuente de verdad que ya usa la
  // búsqueda de paciente directa: usuarios.SRI_RUC), así que funciona para
  // cualquier médico de cualquier clínica al que se le configure RUC propio.
  useEffect(() => {
    if (!turno?.USUARIO_MEDICO) return;
    api.get(`/facturacion/medico-ruc-propio/${turno.USUARIO_MEDICO}`)
      .then(({ data }) => {
        if (data?.tieneRucPropio) {
          setForm(f => ({ ...f, MEDICO_FACTURA: turno.USUARIO_MEDICO }));
        }
      })
      .catch(() => {});
  }, [turno?.USUARIO_MEDICO]);

  // Si la lista de cajas llega después del render inicial (fetch async), sincronizar la selección
  useEffect(() => {
    if (!form.COD_CAJA && cajas.length > 0) {
      setForm(f => ({ ...f, COD_CAJA: cajas[0].COD }));
    }
  }, [cajas]);

  // Buscar paciente si no viene de turno
  const [busqPac, setBusqPac] = useState('');
  const [sugsPac, setSugsPac] = useState([]);
  const [perfilesFacturacion, setPerfilesFacturacion] = useState([]);
  const [modoFactura, setModoFactura] = useState('propio'); // 'propio' | COD del perfil | 'otro'
  const [showNuevoPerfil, setShowNuevoPerfil] = useState(false);
  const PERFIL_VACIO = { C_TIPO_IDENTIFICACION:'CDL', IDENTIFICACION:'', APELLIDOS:'', NOMBRES:'', DIRECCION:'', TELEFONO:'', EMAIL:'' };
  const [nuevoPerfil, setNuevoPerfil] = useState(PERFIL_VACIO);
  const [guardandoPerfil, setGuardandoPerfil] = useState(false);
  const [errPerfil, setErrPerfil] = useState('');
  const [buscandoCedulaPerfil, setBuscandoCedulaPerfil] = useState(false);
  const [msgCedulaPerfil, setMsgCedulaPerfil] = useState('');
  const [buscandoCedulaForm, setBuscandoCedulaForm] = useState(false);
  const [msgCedulaForm, setMsgCedulaForm] = useState('');
  const [datosPacienteOriginal, setDatosPacienteOriginal] = useState(null);
  const [avisoMedicoSugerido, setAvisoMedicoSugerido] = useState('');

  useEffect(() => {
    if (busqPac.length < 2 || turno) { setSugsPac([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/pacientes?q=${encodeURIComponent(busqPac)}&limit=6`); setSugsPac(data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [busqPac, turno]);

  const selPac = p => {
    setSugsPac([]); setBusqPac('');
    const datosOriginales = {
      IDENTIFICACION: p.IDENTIFICACION,
      RAZON_SOCIAL: `${p.APELLIDO_PATERNO||''} ${p.APELLIDO_MATERNO||''} ${p.PRIMER_NOMBRE||''}`.trim(),
      TELEFONO: p.TELEFONO || '',
      EMAIL:    p.EMAIL    || '',
      C_TIPO_IDENTIFICACION: p.C_TIPO_IDENTIFICACION || 'CDL',
    };
    setDatosPacienteOriginal(datosOriginales);
    setModoFactura('propio');
    setForm(f => ({ ...f, COD_PACIENTE: p.COD, ...datosOriginales }));
    setAvisoMedicoSugerido('');
    // Si el paciente tiene HOY un turno (sin facturar todavía) con un médico
    // que factura con su propio RUC, se sugiere automáticamente marcar
    // "Facturar a nombre de" ese médico — para no dejarlo caer en el Centro
    // por descuido, como pasó con la factura #9113.
    api.get(`/facturacion/sugerencia-medico/${p.COD}`).then(({ data }) => {
      if (data?.MEDICO_FACTURA) {
        setForm(f => ({ ...f, MEDICO_FACTURA: data.MEDICO_FACTURA }));
        setAvisoMedicoSugerido(`✓ Se detectó un turno de hoy con ${data.MEDICO_NOMBRE || data.MEDICO_FACTURA} — factura marcada a su nombre. Cambia el selector "Facturar a nombre de" si no corresponde.`);
      }
    }).catch(() => {});
  };

  const cambiarModoFactura = (valor) => {
    if (valor === '__nuevo__') {
      setNuevoPerfil(PERFIL_VACIO); setErrPerfil(''); setMsgCedulaPerfil(''); setShowNuevoPerfil(true);
      return;
    }
    setModoFactura(valor);
    if (valor === 'propio' && datosPacienteOriginal) {
      setForm(f => ({ ...f, ...datosPacienteOriginal }));
    } else if (valor === 'otro') {
      setForm(f => ({ ...f, IDENTIFICACION:'', RAZON_SOCIAL:'', TELEFONO:'', EMAIL:'' }));
    } else {
      const perfil = perfilesFacturacion.find(pf => String(pf.COD) === String(valor));
      aplicarPerfilFacturacion(perfil);
    }
  };

  useEffect(() => {
    if (!form.COD_PACIENTE) { setPendientes([]); setPendSel([]); setPerfilesFacturacion([]); setModoFactura('propio'); return; }
    api.get(`/facturacion/consumos/${form.COD_PACIENTE}`)
      .then(r => { setPendientes(r.data||[]); setPendSel((r.data||[]).filter(p=>p.C_ESTADO==='PND').map(p=>p.COD)); })
      .catch(()=>setPendientes([]));
    api.get(`/pacientes/${form.COD_PACIENTE}/facturacion`)
      .then(r => setPerfilesFacturacion(r.data||[]))
      .catch(()=>setPerfilesFacturacion([]));
  }, [form.COD_PACIENTE]);

  // Anula un cargo pendiente (ej. el paciente no se hizo el examen aquí) —
  // nunca toca cargos ya facturados, esos se manejan con nota de crédito.
  const anularPendiente = async (cod) => {
    try {
      await api.patch(`/facturacion/consumos/${cod}/anular`, { motivo: 'Anulado por usuario' });
      const r = await api.get(`/facturacion/consumos/${form.COD_PACIENTE}`);
      setPendientes(r.data||[]);
      setPendSel((r.data||[]).filter(p=>p.C_ESTADO==='PND').map(p=>p.COD));
    } catch(e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const aplicarPerfilFacturacion = (perfil) => {
    if (!perfil) return;
    setForm(f => ({
      ...f,
      IDENTIFICACION: perfil.IDENTIFICACION,
      RAZON_SOCIAL: `${perfil.APELLIDOS||''} ${perfil.NOMBRES||''}`.trim(),
      C_TIPO_IDENTIFICACION: perfil.C_TIPO_IDENTIFICACION || 'CDL',
      TELEFONO: perfil.TELEFONO || '',
      EMAIL: perfil.EMAIL || '',
    }));
  };
  const buscarCedulaPerfil = async () => {
    const cedula = (nuevoPerfil.IDENTIFICACION || '').trim();
    if (!/^\d{10}$/.test(cedula)) { setMsgCedulaPerfil('Ingresa una cédula válida de 10 dígitos'); return; }
    setBuscandoCedulaPerfil(true); setMsgCedulaPerfil('');
    try {
      const { data } = await api.get(`/pacientes/consultar-cedula/${cedula}`);
      setNuevoPerfil(p => ({
        ...p,
        APELLIDOS: `${data.APELLIDO_PATERNO||''} ${data.APELLIDO_MATERNO||''}`.trim(),
        NOMBRES: `${data.PRIMER_NOMBRE||''} ${data.SEGUNDO_NOMBRE||''}`.trim(),
      }));
      setMsgCedulaPerfil('✓ Datos completados');
    } catch (e) {
      setMsgCedulaPerfil(e.response?.data?.error || 'No se pudo consultar la cédula');
    }
    setBuscandoCedulaPerfil(false);
  };
  // Autocompletar por cédula del formulario principal (Razón Social directa,
  // sin pasar por "Agregar nuevo perfil de facturación"). Usa el mismo
  // endpoint /pacientes/consultar-cedula que ya existe.
  const buscarCedulaForm = async () => {
    const cedula = (form.IDENTIFICACION || '').trim();
    if (!/^\d{10}$/.test(cedula)) { setMsgCedulaForm('Ingresa una cédula válida de 10 dígitos'); return; }
    setBuscandoCedulaForm(true); setMsgCedulaForm('');
    try {
      const { data } = await api.get(`/pacientes/consultar-cedula/${cedula}`);
      const nombreCompleto = `${data.APELLIDO_PATERNO||''} ${data.APELLIDO_MATERNO||''} ${data.PRIMER_NOMBRE||''} ${data.SEGUNDO_NOMBRE||''}`
        .replace(/\s+/g, ' ').trim();
      setForm(f => ({ ...f, RAZON_SOCIAL: nombreCompleto }));
      setMsgCedulaForm('✓ Datos completados');
    } catch (e) {
      setMsgCedulaForm(e.response?.data?.error || 'No se pudo consultar la cédula');
    }
    setBuscandoCedulaForm(false);
  };
  const guardarNuevoPerfil = async () => {
    if (!nuevoPerfil.IDENTIFICACION || !nuevoPerfil.APELLIDOS || !nuevoPerfil.NOMBRES) {
      setErrPerfil('Identificación, apellidos y nombres son obligatorios'); return;
    }
    const errorId = validarIdentificacion(nuevoPerfil.C_TIPO_IDENTIFICACION, nuevoPerfil.IDENTIFICACION);
    if (errorId) { setErrPerfil(errorId); return; }
    setGuardandoPerfil(true); setErrPerfil('');
    try {
      await api.post(`/pacientes/${form.COD_PACIENTE}/facturacion`, nuevoPerfil);
      const { data } = await api.get(`/pacientes/${form.COD_PACIENTE}/facturacion`);
      setPerfilesFacturacion(data || []);
      const creado = (data || []).find(pf => pf.IDENTIFICACION === nuevoPerfil.IDENTIFICACION);
      if (creado) {
        setModoFactura(String(creado.COD));
        aplicarPerfilFacturacion(creado);
      }
      setShowNuevoPerfil(false);
    } catch (e) {
      setErrPerfil(e.response?.data?.error || e.message);
    }
    setGuardandoPerfil(false);
  };

  const togglePend = cod => setPendSel(s => s.includes(cod) ? s.filter(x=>x!==cod) : [...s,cod]);

  const agregarPendientesSel = () => {
    const seleccionados = pendientes.filter(p => p.C_ESTADO==='PND' && pendSel.includes(p.COD) && !consumosIds.includes(p.COD));
    if (!seleccionados.length) return;
    setDetalles(d => [...d, ...seleccionados.map(p => ({
      DESCRIPCION:    p.DESCRIPCION||'Examen',
      CANTIDAD:       Number(p.CANTIDAD)||1,
      VALOR:          Number(p.VALOR)||0,
      DESCUENTO:      Number(p.DESCUENTO)||0,
      PORCENTAJE_IVA: Number(p.PORCENTAJE_IVA)||0,
      COD_CONSUMO:    p.COD,
      _consumoId:     p.COD,
    }))]);
    setConsumosIds(ids => [...ids, ...seleccionados.map(p=>p.COD)]);
  };

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const setD = (i,k,v) => setDetalles(d=>d.map((r,j)=>j===i?{...r,[k]:v}:r));
  const setP = (i,k,v) => setPagos(p=>p.map((r,j)=>j===i?{...r,[k]:v}:r));

  const verHistorial = async () => {
    setTab('historial');
    if (!form.COD_PACIENTE) return;
    if (historial!==null && !histErr) return;
    setLoadingHist(true); setHistErr('');
    try {
      const { data } = await api.get(`/facturacion/historial-paciente?cod_paciente=${form.COD_PACIENTE}`);
      setHistorial(data);
    } catch(e) { setHistErr(e.response?.data?.error || e.message); setHistorial([]); }
    setLoadingHist(false);
  };

  const agregarServicio = svc => {
    const iva = svc.APLICA_IVA ? ivaGlobal : 0;
    setDetalles(d=>[...d,{
      DESCRIPCION: svc.SERVICIO, CANTIDAD:1,
      VALOR: Number(svc.VALOR)||0, DESCUENTO:0,
      PORCENTAJE_IVA: iva,
      COD_CONSUMO: null, // svc.COD es del catálogo `servicios`, no un registro real de `consumos` (FK)
    }]);
  };

  // Al facturar un medicamento directamente descuenta stock real y se
  // registra en el Kardex de Farmacia (igual que una dispensación) — el
  // backend valida el stock de nuevo al guardar, esto es solo referencial.
  const agregarMedicamento = med => {
    setDetalles(d=>[...d,{
      DESCRIPCION: med.MEDICAMENTO + (med.NOMBRE_COMERCIAL ? ` (${med.NOMBRE_COMERCIAL})` : ''),
      CANTIDAD: 1,
      VALOR: Number(med.PRECIO_VENTA)||0, // PVP al momento de agregar; el campo queda editable por si cambió
      DESCUENTO: 0,
      PORCENTAJE_IVA: 0,
      COD_CONSUMO: null,
      COD_MEDICAMENTO: med.COD,
      _stockDisponible: Number(med.STOCK)||0,
    }]);
  };

  // ── Buscador unificado de servicios + medicamentos ──────────────────────
  const [buscadorQ, setBuscadorQ] = useState('');
  const [sugsBuscador, setSugsBuscador] = useState([]);
  const [buscandoMed, setBuscandoMed] = useState(false);

  useEffect(() => {
    if (buscadorQ.trim().length < 2) { setSugsBuscador([]); return; }
    const q = buscadorQ.trim().toLowerCase();
    const sugsServ = servicios
      .filter(s => s.SERVICIO?.toLowerCase().includes(q))
      .slice(0, 6)
      .map(s => ({ tipo:'SERVICIO', item:s }));

    let cancelado = false;
    setBuscandoMed(true);
    const t = setTimeout(async () => {
      let sugsMed = [];
      try {
        const { data } = await api.get(`/farmacia/medicamentos?q=${encodeURIComponent(buscadorQ.trim())}&vendible=1&limit=6`);
        sugsMed = (data||[]).map(m => ({ tipo:'MEDICAMENTO', item:m }));
      } catch {}
      if (!cancelado) { setSugsBuscador([...sugsServ, ...sugsMed]); setBuscandoMed(false); }
    }, 280);
    return () => { cancelado = true; clearTimeout(t); };
  }, [buscadorQ, servicios]);

  const elegirSugerencia = sug => {
    // Los medicamentos siempre deben ir a nombre del Centro (Farmacia,
    // punto 500) — nunca por el RUC personal de un médico (mismo error
    // que causó la factura #9119, facturada por error bajo el RUC del
    // Dr. Valdivieso). Se avisa aquí antes de guardar, aunque el backend
    // también lo bloquea como respaldo.
    if (sug.tipo === 'MEDICAMENTO' && form.MEDICO_FACTURA) {
      setErr('Los medicamentos no se pueden agregar cuando "Facturar a nombre de" está en un médico con RUC propio. Cambia el selector a Centro, o factura el medicamento en un documento aparte.');
      return;
    }
    if (sug.tipo === 'SERVICIO') agregarServicio(sug.item);
    else agregarMedicamento(sug.item);
    setBuscadorQ(''); setSugsBuscador([]);
  };

  // Calculos
  const subtotal0  = detalles.reduce((s,d)=>s+(Number(d.VALOR)||0)*(Number(d.CANTIDAD)||1)-(Number(d.DESCUENTO)||0),0);
  const totalIva   = detalles.reduce((s,d)=>{
    const base=(Number(d.VALOR)||0)*(Number(d.CANTIDAD)||1)-(Number(d.DESCUENTO)||0);
    return s+base*(Number(d.PORCENTAJE_IVA)||0)/100;
  },0);
  const totalDesc  = detalles.reduce((s,d)=>s+(Number(d.DESCUENTO)||0),0);
  const total      = subtotal0+totalIva;
  const totalPagos = pagos.reduce((s,p)=>s+Number(p.VALOR||0),0);
  const subtotalServicios    = detalles.filter(d=>!d.COD_MEDICAMENTO).reduce((s,d)=>s+(Number(d.VALOR)||0)*(Number(d.CANTIDAD)||1)-(Number(d.DESCUENTO)||0),0);
  const subtotalMedicamentos = detalles.filter(d=>d.COD_MEDICAMENTO).reduce((s,d)=>s+(Number(d.VALOR)||0)*(Number(d.CANTIDAD)||1)-(Number(d.DESCUENTO)||0),0);
  // Stock afectado por medicamento (referencial en pantalla — el backend
  // vuelve a validar el stock real al guardar). Si el mismo medicamento se
  // agrega en más de una línea, se suman las cantidades sobre el stock que
  // tenía al momento de agregarlo.
  const stockAfectado = Object.values(detalles.filter(d=>d.COD_MEDICAMENTO).reduce((acc,d)=>{
    const key = d.COD_MEDICAMENTO;
    if (!acc[key]) acc[key] = { nombre: d.DESCRIPCION, inicial: d._stockDisponible ?? 0, usado: 0 };
    acc[key].usado += Number(d.CANTIDAD)||0;
    return acc;
  }, {}));

  const guardar = async () => {
    if (!form.COD_PACIENTE) { setErr('Seleccione un paciente'); return; }
    if (!form.RAZON_SOCIAL) { setErr('Razon social es obligatoria'); return; }
    if (detalles.length===0){ setErr('Agregue al menos un producto'); return; }
    if (pagos.length===0)   { setErr('Agregue al menos una forma de pago'); return; }
    if (Math.abs(totalPagos-total)>0.01){ setErr(`Total pagos ($${totalPagos.toFixed(2)}) no coincide con total ($${total.toFixed(2)})`); return; }
    const sinStock = detalles.find(d => d.COD_MEDICAMENTO && d._stockDisponible!=null && Number(d.CANTIDAD) > d._stockDisponible);
    if (sinStock) { setErr(`"${sinStock.DESCRIPCION}": la cantidad (${sinStock.CANTIDAD}) supera el stock disponible (${sinStock._stockDisponible})`); return; }
    if (form.IDENTIFICACION) {
      const errorId = validarIdentificacion(form.C_TIPO_IDENTIFICACION, form.IDENTIFICACION);
      if (errorId) { setErr(errorId); return; }
    }
    setSaving(true); setErr('');
    try {
      const { data } = await api.post('/facturacion', {
        ...form, detalles, pagos,
        COD_TURNO: turno?.COD,
        consumos_ids: consumosIds,
      });
      const sri = data?.sri;
      if (sri?.estado === 'AUTORIZADO') {
        setSriInfo({ ok: true, msg: '✅ Autorizada por el SRI — N° ' + sri.numero_autorizacion });
        setTimeout(() => onGuardar(), 2500);
      } else if (sri?.errores?.length) {
        setSriInfo({ ok: false, msg: '⚠️ Factura guardada pero el SRI devolvió: ' + sri.errores.join(' | ') });
        setTimeout(() => onGuardar(), 4000);
      } else if (sri?.detalle) {
        setSriInfo({ ok: false, msg: '⚠️ SRI pendiente: ' + sri.detalle });
        setTimeout(() => onGuardar(), 3000);
      } else {
        onGuardar();
      }
    } catch(e){ setErr(e.response?.data?.error||e.message); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="fpage-header">
        <div>
          <div className="crumb">Facturación → Nueva factura</div>
          <h2>Registrar documento</h2>
        </div>
        <button className="fpage-back" title="Volver al listado" onClick={onCerrar}>←</button>
      </div>
      <div className="fpage-body">
        <div className="mbd" style={{padding:0}}>
          {/* Cabecera */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:10,marginBottom:12}}>
            <div>
              <label className="form-label" style={{fontSize:10}}>Fecha</label>
              <div style={{fontSize:12,padding:'5px 0'}}>{new Date().toLocaleDateString('es-EC')}</div>
            </div>
            <div>
              <label className="form-label" style={{fontSize:10}}>Caja</label>
              <select className="form-control" style={{fontSize:12}} value={form.COD_CAJA} onChange={e=>set('COD_CAJA',e.target.value)}>
                {cajas.map(c=><option key={c.COD} value={c.COD}>{c.CAJA}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label" style={{fontSize:10}}>Tipo identificacion</label>
              <select className="form-control" style={{fontSize:12}} value={form.C_TIPO_IDENTIFICACION} onChange={e=>set('C_TIPO_IDENTIFICACION',e.target.value)}>
                <option value="CDL">Cedula</option>
                <option value="RUC">RUC</option>
                <option value="PSP">Pasaporte</option>
                <option value="NAP">N/A</option>
              </select>
            </div>
            <div>
              <label className="form-label" style={{fontSize:10}}>Facturar a nombre de</label>
              <select className="form-control" style={{fontSize:12}} value={form.MEDICO_FACTURA||''} onChange={e=>{
                const nuevoValor = e.target.value || null;
                // Si ya hay medicamentos en el carrito, no dejar cambiar a un
                // médico con RUC propio — deben facturarse aparte, a nombre
                // del Centro (mismo error de la factura #9119).
                if (nuevoValor && detalles.some(d => d.COD_MEDICAMENTO)) {
                  setErr('Esta factura ya tiene medicamentos agregados — no se puede facturar a nombre de un médico con RUC propio. Quita los medicamentos primero, o factúralos en un documento aparte.');
                  return;
                }
                set('MEDICO_FACTURA', nuevoValor);
              }}>
                <option value="">Centro (Caroll Bombon)</option>
                <option value="GVALDIVIESO">Dr. Gustavo Valdivieso</option>
              </select>
            </div>
          </div>

          {avisoMedicoSugerido&&(
            <div style={{background:'var(--blue-lt)',color:'var(--navy)',padding:'8px 10px',borderRadius:6,fontSize:12,marginBottom:12,fontWeight:600}}>
              {avisoMedicoSugerido}
            </div>
          )}

          {cajaAbierta===false&&(
            <div style={{background:'var(--red-lt)',color:'var(--red)',padding:'8px 10px',borderRadius:6,fontSize:12,marginBottom:12,fontWeight:600}}>
              ⚠️ Esta caja no tiene un turno abierto. Ábrela desde "🔒 Cierre de Caja" antes de facturar.
            </div>
          )}

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
            <div>
              <label className="form-label" style={{fontSize:10}}>Razon social *</label>
              {turno
                ? <div style={{fontSize:12,fontWeight:600,padding:'5px 0'}}>{form.RAZON_SOCIAL}</div>
                : <div style={{position:'relative'}}>
                    {form.COD_PACIENTE
                      ? (modoFactura==='otro'
                          ? <>
                              <input className="form-control" style={{fontSize:12}} placeholder="Nombre de quien factura..." value={form.RAZON_SOCIAL} onChange={e=>set('RAZON_SOCIAL',e.target.value)} />
                              {/* Aviso de seguridad: esta factura queda internamente vinculada
                                  al paciente de datosPacienteOriginal (para su historia/receta),
                                  aunque se facture a nombre de otra persona. Antes esto no se
                                  mostraba en ningún lado, así que si alguien buscaba un paciente
                                  por error y luego escribía otro nombre aquí, la factura quedaba
                                  "cruzada" sin que nadie lo notara (caso facturas #9111-9113). */}
                              {datosPacienteOriginal&&(
                                <div style={{fontSize:11,color:'var(--text3)',marginTop:3}}>
                                  Consulta vinculada al paciente: <strong>{datosPacienteOriginal.RAZON_SOCIAL}</strong>
                                  {' '}— si no corresponde, usa el buscador de abajo en vez de escribir aquí.
                                </div>
                              )}
                            </>
                          : <div style={{display:'flex',alignItems:'center',gap:6,background:'var(--blue-lt)',borderRadius:5,padding:'5px 8px',fontSize:12}}>
                              <span style={{flex:1,fontWeight:600}}>{form.RAZON_SOCIAL}</span>
                              <span style={{cursor:'pointer'}} onClick={()=>{setForm(f=>({...f,COD_PACIENTE:'',RAZON_SOCIAL:'',IDENTIFICACION:''}));setModoFactura('propio');setPerfilesFacturacion([]);}}>&#x2715;</span>
                            </div>
                        )
                      : <>
                          <input className="form-control" style={{fontSize:12}} placeholder="Buscar paciente..." value={busqPac} onChange={e=>setBusqPac(e.target.value)} />
                          {sugsPac.length>0&&(
                            <div style={{position:'absolute',top:'100%',left:0,right:0,background:'#fff',border:'1px solid var(--border)',borderRadius:5,zIndex:10,maxHeight:160,overflowY:'auto',boxShadow:'0 4px 12px rgba(0,0,0,.1)'}}>
                              {sugsPac.map(p=>(
                                <div key={p.COD} onClick={()=>selPac(p)} style={{padding:'6px 10px',fontSize:12,cursor:'pointer',borderBottom:'1px solid var(--bg)'}}
                                  onMouseEnter={e=>e.currentTarget.style.background='var(--blue-lt)'}
                                  onMouseLeave={e=>e.currentTarget.style.background=''}>
                                  <strong>{p.NOMBRE_COMPLETO?.trim()}</strong> <span style={{color:'var(--text3)',marginLeft:6}}>{p.IDENTIFICACION}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                    }
                  </div>
              }
              {(turno||form.COD_PACIENTE)&&(
                <select className="form-control" style={{fontSize:11,marginTop:5}} value={modoFactura} onChange={e=>cambiarModoFactura(e.target.value)}>
                  <option value="propio">Facturar al paciente</option>
                  {perfilesFacturacion.map(pf=>(
                    <option key={pf.COD} value={pf.COD}>{pf.APELLIDOS} {pf.NOMBRES} — {pf.IDENTIFICACION}</option>
                  ))}
                  <option value="otro">Otra persona/empresa (escribir)...</option>
                  <option value="__nuevo__">+ Agregar nuevo perfil de facturación...</option>
                </select>
              )}
              {showNuevoPerfil && (
                <div className="mb" style={{zIndex:1100}} onClick={e=>e.target===e.currentTarget&&setShowNuevoPerfil(false)}>
                  <div className="mx" style={{maxWidth:420}}>
                    <div className="mh" style={{background:'var(--navy)',color:'#fff'}}>
                      <span style={{fontWeight:700,fontSize:13}}>Nuevo perfil de facturación</span>
                      <span style={{cursor:'pointer',fontSize:18,opacity:.8}} onClick={()=>setShowNuevoPerfil(false)}>&#x2715;</span>
                    </div>
                    <div className="mbd">
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                        <div>
                          <label className="form-label" style={{fontSize:10}}>Tipo identificación</label>
                          <select className="form-control" style={{fontSize:12}} value={nuevoPerfil.C_TIPO_IDENTIFICACION}
                            onChange={e=>setNuevoPerfil(p=>({...p,C_TIPO_IDENTIFICACION:e.target.value}))}>
                            <option value="CDL">Cedula</option>
                            <option value="RUC">RUC</option>
                            <option value="PSP">Pasaporte</option>
                            <option value="NAP">N/A</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label" style={{fontSize:10}}>Identificación *</label>
                          <input className="form-control" style={{fontSize:12}} value={nuevoPerfil.IDENTIFICACION}
                            onChange={e=>{
                              const val = e.target.value;
                              // Si el largo del número corresponde a un tipo distinto al
                              // seleccionado (10 = cédula, 13 = RUC), lo corrige solo —
                              // evita el error de la #9122 (RUC guardado como "Cédula").
                              const tipoSugerido = tipoSugeridoPorLargo(val);
                              setNuevoPerfil(p=>({...p, IDENTIFICACION: val,
                                C_TIPO_IDENTIFICACION: tipoSugerido || p.C_TIPO_IDENTIFICACION}));
                            }} />
                          {nuevoPerfil.C_TIPO_IDENTIFICACION==='CDL' && (
                            <>
                              <button type="button" className="btn btn-sm" style={{marginTop:4,fontSize:10,padding:'2px 6px'}}
                                disabled={buscandoCedulaPerfil || !/^\d{10}$/.test(nuevoPerfil.IDENTIFICACION||'')}
                                onClick={buscarCedulaPerfil}>
                                {buscandoCedulaPerfil ? 'Buscando...' : '🔍 Autocompletar'}
                              </button>
                              {msgCedulaPerfil && <div style={{fontSize:10,marginTop:2,color:msgCedulaPerfil.startsWith('✓')?'var(--green)':'var(--red)'}}>{msgCedulaPerfil}</div>}
                            </>
                          )}
                        </div>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                        <div>
                          <label className="form-label" style={{fontSize:10}}>Apellidos *</label>
                          <input className="form-control" style={{fontSize:12}} value={nuevoPerfil.APELLIDOS}
                            onChange={e=>setNuevoPerfil(p=>({...p,APELLIDOS:e.target.value}))} />
                        </div>
                        <div>
                          <label className="form-label" style={{fontSize:10}}>Nombres *</label>
                          <input className="form-control" style={{fontSize:12}} value={nuevoPerfil.NOMBRES}
                            onChange={e=>setNuevoPerfil(p=>({...p,NOMBRES:e.target.value}))} />
                        </div>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                        <div>
                          <label className="form-label" style={{fontSize:10}}>Teléfono</label>
                          <input className="form-control" style={{fontSize:12}} value={nuevoPerfil.TELEFONO}
                            onChange={e=>setNuevoPerfil(p=>({...p,TELEFONO:e.target.value}))} />
                        </div>
                        <div>
                          <label className="form-label" style={{fontSize:10}}>Email</label>
                          <input className="form-control" style={{fontSize:12}} value={nuevoPerfil.EMAIL}
                            onChange={e=>setNuevoPerfil(p=>({...p,EMAIL:e.target.value}))} />
                        </div>
                      </div>
                      <div style={{marginBottom:10}}>
                        <label className="form-label" style={{fontSize:10}}>Dirección</label>
                        <input className="form-control" style={{fontSize:12}} value={nuevoPerfil.DIRECCION}
                          onChange={e=>setNuevoPerfil(p=>({...p,DIRECCION:e.target.value}))} />
                      </div>
                      {errPerfil && <div style={{color:'var(--red)',fontSize:11,marginBottom:10}}>{errPerfil}</div>}
                      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                        <button className="btn btn-sm" onClick={()=>setShowNuevoPerfil(false)}>Cancelar</button>
                        <button className="btn btn-primary btn-sm" disabled={guardandoPerfil} onClick={guardarNuevoPerfil}>
                          {guardandoPerfil ? 'Guardando...' : 'Guardar perfil'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="form-label" style={{fontSize:10}}>Identificacion</label>
              <input className="form-control" style={{fontSize:12}} value={form.IDENTIFICACION} onChange={e=>{
                const val = e.target.value;
                // Autocorrección de tipo por largo (10=cédula, 13=RUC) — mismo
                // resguardo que evita el error de la factura #9122.
                const tipoSugerido = tipoSugeridoPorLargo(val);
                setForm(f=>({...f, IDENTIFICACION: val,
                  C_TIPO_IDENTIFICACION: tipoSugerido || f.C_TIPO_IDENTIFICACION}));
              }} />
              {form.C_TIPO_IDENTIFICACION==='CDL' && (
                <>
                  <button type="button" className="btn btn-sm" style={{marginTop:4,fontSize:10,padding:'2px 6px'}}
                    disabled={buscandoCedulaForm || !/^\d{10}$/.test(form.IDENTIFICACION||'')}
                    onClick={buscarCedulaForm}>
                    {buscandoCedulaForm ? 'Buscando...' : '🔍 Autocompletar con cédula'}
                  </button>
                  {msgCedulaForm && <div style={{fontSize:10,marginTop:2,color:msgCedulaForm.startsWith('✓')?'var(--green)':'var(--red)'}}>{msgCedulaForm}</div>}
                </>
              )}
            </div>
            <div>
              <label className="form-label" style={{fontSize:10}}>Telefono</label>
              <input className="form-control" style={{fontSize:12}} value={form.TELEFONO||''} onChange={e=>set('TELEFONO',e.target.value)} />
            </div>
            <div>
              <label className="form-label" style={{fontSize:10}}>Email</label>
              <input type="email" className="form-control" style={{fontSize:12}} value={form.EMAIL||''} onChange={e=>set('EMAIL',e.target.value)} />
            </div>
            <div>
              <label className="form-label" style={{fontSize:10}}>Dirección</label>
              <input className="form-control" style={{fontSize:12}} value={form.DIRECCION||''} onChange={e=>set('DIRECCION',e.target.value)} />
            </div>
            <div>
              <label className="form-label" style={{fontSize:10}}>Observacion</label>
              <input className="form-control" style={{fontSize:12}} value={form.OBSERVACION||''} onChange={e=>set('OBSERVACION',e.target.value)} />
            </div>
          </div>

          {/* Pendientes de facturar */}
          {pendientes.length>0&&(
            <div style={{border:'1px solid var(--amber)',background:'var(--amber-lt)',borderRadius:8,padding:12,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:600,color:'var(--amber)',marginBottom:8}}>
                📋 Exámenes pendientes de facturar ({pendientes.length})
              </div>
              {pendientes.map(p=>{
                const yaAgregado = consumosIds.includes(p.COD);
                return (
                  <div key={p.COD} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0'}}>
                    <label style={{display:'flex',alignItems:'center',gap:8,flex:1,fontSize:12,cursor:yaAgregado?'default':'pointer',opacity:yaAgregado?0.5:1}}>
                      <input type="checkbox" disabled={yaAgregado} checked={pendSel.includes(p.COD)} onChange={()=>togglePend(p.COD)} style={{width:14,height:14,cursor:'pointer'}}/>
                      <span style={{flex:1}}>{p.DESCRIPCION}{yaAgregado&&' ✓ agregado'}</span>
                      <span style={{fontWeight:600}}>{fmt((Number(p.VALOR)||0)*(Number(p.CANTIDAD)||1) - (Number(p.DESCUENTO)||0))}</span>
                    </label>
                    {!yaAgregado && p.C_ESTADO==='PND' && (
                      <button type="button" title="El paciente no se hizo esto en este centro"
                        style={{fontSize:11,color:'var(--red, #b03030)',background:'none',border:'none',cursor:'pointer',padding:'2px 6px'}}
                        onClick={()=>anularPendiente(p.COD)}>
                        Anular
                      </button>
                    )}
                  </div>
                );
              })}
              <button className="btn btn-sm" style={{marginTop:8,background:'var(--navy)',color:'#fff',border:'none'}}
                onClick={agregarPendientesSel}>
                + Agregar seleccionados a la factura
              </button>
            </div>
          )}

          <div className="fac-grid">
            <div className="fac-main">
              {/* Tabs */}
              <div className="tab-btns">
                <button className={`tab-btn ${tab==='productos'?'on':''}`} onClick={()=>setTab('productos')}>Servicios y medicamentos</button>
                <button className={`tab-btn ${tab==='historial'?'on':''}`} onClick={verHistorial}>Historial</button>
              </div>

              {/* Tab Productos */}
              {tab==='productos'&&(
                <div style={{paddingTop:12}}>
                  <div className="buscador-item">
                    <span className="ic-search">🔍</span>
                    <input
                      placeholder="Buscar servicio o medicamento por nombre..."
                      value={buscadorQ}
                      onChange={e=>setBuscadorQ(e.target.value)}
                    />
                    {buscadorQ.trim().length>=2&&(sugsBuscador.length>0||buscandoMed)&&(
                      <div className="buscador-drop">
                        {sugsBuscador.length===0&&buscandoMed&&(
                          <div style={{padding:'8px 10px',fontSize:11,color:'var(--text3)'}}>Buscando...</div>
                        )}
                        {sugsBuscador.map((sug,idx)=>{
                          const esMed = sug.tipo==='MEDICAMENTO';
                          const stock = esMed ? Number(sug.item.STOCK)||0 : null;
                          return (
                            <div key={idx} className="buscador-row" onClick={()=>elegirSugerencia(sug)}>
                              <span className={`bi ${esMed?'med':'serv'}`}>{esMed?'💊':'🩺'}</span>
                              <div style={{flex:1,minWidth:0}}>
                                <div className="bn">{esMed ? sug.item.MEDICAMENTO + (sug.item.NOMBRE_COMERCIAL?` (${sug.item.NOMBRE_COMERCIAL})`:'') : sug.item.SERVICIO}</div>
                                <div className="bm">{esMed?'Medicamento · Farmacia':'Servicio'}</div>
                              </div>
                              <div>
                                <div className="bp">{fmt(esMed?sug.item.PRECIO_VENTA:sug.item.VALOR)}</div>
                                {esMed&&<div className={`bs ${stock>0?'ok':'low'}`}>Stock: {stock}</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <table className="items-tbl">
                    <thead><tr><th style={{width:80}}>Tipo</th><th>Producto</th><th style={{width:70}}>Precio</th><th style={{width:60}}>Cant.</th><th style={{width:55}}>Des.%</th><th style={{width:60}}>Des.$</th><th style={{width:75}}>Total</th><th style={{width:28}}></th></tr></thead>
                    <tbody>
                      {detalles.length===0&&<tr><td colSpan={8} style={{textAlign:'center',color:'var(--text3)',padding:'16px 0'}}>Sin productos — usa el buscador de arriba</td></tr>}
                      {detalles.map((d,i)=>{
                        const tot=(Number(d.VALOR)||0)*(Number(d.CANTIDAD)||1)-(Number(d.DESCUENTO)||0);
                        const esMed = !!d.COD_MEDICAMENTO;
                        const excedeStock = esMed && d._stockDisponible!=null && Number(d.CANTIDAD) > d._stockDisponible;
                        return (
                          <tr key={i}>
                            <td><span className={`tipo-badge ${esMed?'med':'serv'}`}>{esMed?'Medicam.':'Servicio'}</span></td>
                            <td>
                              <input style={{width:'100%',border:'none',background:'transparent',fontSize:12,fontWeight:600}} value={d.DESCRIPCION} onChange={e=>setD(i,'DESCRIPCION',e.target.value)} />
                              {esMed && (
                                excedeStock
                                  ? <div className="stock-nota warn">⚠ Solo {d._stockDisponible} en stock</div>
                                  : <div className="stock-nota ok">✓ Stock: {d._stockDisponible}</div>
                              )}
                            </td>
                            <td><input type="number" style={{width:55,...inp}} min={0} step=".01" value={d.VALOR} onChange={e=>setD(i,'VALOR',Number(e.target.value))} /></td>
                            <td><input type="number" style={{width:40,...inp}} min={1} value={d.CANTIDAD} onChange={e=>setD(i,'CANTIDAD',Number(e.target.value))} /></td>
                            <td><input type="number" style={{width:40,...inp,background:'var(--surface)',cursor:'default'}} min={0} max={100} value={d.PORCENTAJE_IVA} readOnly /></td>
                            <td><input type="number" style={{width:50,...inp}} min={0} step=".01" value={d.DESCUENTO} onChange={e=>setD(i,'DESCUENTO',Number(e.target.value))} /></td>
                            <td style={{fontWeight:700,fontSize:12,color:'var(--navy)'}}>{fmt(tot)}</td>
                            <td><button onClick={()=>{
                              const fila=detalles[i];
                              setDetalles(d=>d.filter((_,j)=>j!==i));
                              if(fila._consumoId){
                                setConsumosIds(ids=>ids.filter(x=>x!==fila._consumoId));
                                setPendSel(s=>s.filter(x=>x!==fila._consumoId));
                              }
                            }} style={{background:'var(--red)',color:'#fff',border:'none',borderRadius:3,padding:'2px 5px',cursor:'pointer',fontSize:10}}>&#x2715;</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tab Historial */}
              {tab==='historial'&&(
                <div style={{paddingTop:12}}>
                  {histErr&&<div style={{background:'var(--red-lt)',color:'var(--red)',padding:8,borderRadius:6,fontSize:12,marginBottom:10}}>{histErr}</div>}
                  {loadingHist&&<div style={{textAlign:'center',padding:16,color:'var(--text3)',fontSize:12}}>Cargando historial...</div>}
                  {!loadingHist&&historial&&historial.length===0&&(
                    <div style={{textAlign:'center',padding:16,color:'var(--text3)',fontSize:12}}>Sin facturas previas para este paciente</div>
                  )}
                  {!loadingHist&&historial&&historial.length>0&&(
                    <table className="items-tbl">
                      <thead><tr>
                        <th>Fecha</th><th>N° Factura</th><th>Estado</th><th style={{textAlign:'right'}}>Total</th>
                      </tr></thead>
                      <tbody>
                        {historial.map(h=>{
                          const est=ESTADOS[h.C_ESTADO]||ESTADOS.ACT;
                          return (
                            <tr key={h.COD}>
                              <td style={{fontSize:11}}>{h.FECHA?new Date(h.FECHA).toLocaleDateString('es-EC'):'—'}</td>
                              <td style={{fontSize:11}}>
                                {h.SECUENCIAL_SRI
                                  ? `${h.ESTAB||'001'}-001-${String(h.SECUENCIAL_SRI).padStart(9,'0')}`
                                  : `#${h.COD}`}
                              </td>
                              <td><span style={{background:est.bg,color:est.fg,padding:'1px 6px',borderRadius:3,fontSize:9,fontWeight:600}}>{est.label}</span></td>
                              <td style={{textAlign:'right',fontWeight:600,fontSize:12}}>{fmt(h.TOTAL)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            {/* ── Sidebar: resumen, forma de pago, stock afectado, acciones ── */}
            <div className="fac-side">
              <div className="side-card">
                <h4>Resumen del documento</h4>
                <div className="sum-row2"><span>Subtotal servicios</span><span>{fmt(subtotalServicios)}</span></div>
                <div className="sum-row2"><span>Subtotal medicamentos</span><span>{fmt(subtotalMedicamentos)}</span></div>
                <div className="sum-row2"><span>Descuento</span><span>{fmt(totalDesc)}</span></div>
                <div className="sum-row2"><span>IVA</span><span>{fmt(totalIva)}</span></div>
                <div className="sum-row2 total"><span>Total</span><span>{fmt(total)}</span></div>
              </div>

              <div className="side-card">
                <h4>Forma de pago</h4>
                {pagos.length===0&&(
                  <div style={{textAlign:'center',color:'var(--text3)',fontSize:11,padding:'14px 6px',border:'1px dashed var(--border2)',borderRadius:6,marginBottom:8}}>
                    <div style={{fontSize:20,marginBottom:4}}>💳</div>
                    Sin formas de pago registradas
                  </div>
                )}
                {pagos.length>0&&(
                  <table className="pago-tbl-mini">
                    <tbody>
                      {pagos.map((p,i)=>{
                        const fp = formasPago.find(f=>String(f.COD)===String(p.COD_FORMA_DE_PAGO));
                        return (
                        <tr key={i}>
                          <td style={{width:24,fontSize:16,textAlign:'center'}}>{iconoFormaPago(fp?.FORMA_DE_PAGO)}</td>
                          <td style={{width:20}}>
                            <button onClick={()=>setPagos(p=>p.filter((_,j)=>j!==i))} style={{background:'var(--red)',color:'#fff',border:'none',borderRadius:3,padding:'2px 5px',cursor:'pointer',fontSize:9}}>&#x2715;</button>
                          </td>
                          <td>
                            <select style={{...inp,width:'100%',marginBottom:3}} value={p.COD_FORMA_DE_PAGO} onChange={e=>setP(i,'COD_FORMA_DE_PAGO',e.target.value)}>
                              {formasPago.map(f=><option key={f.COD} value={f.COD}>{iconoFormaPago(f.FORMA_DE_PAGO)} {f.FORMA_DE_PAGO}</option>)}
                            </select>
                            <input style={{...inp,width:'100%'}} value={p.ADICIONAL||''} onChange={e=>setP(i,'ADICIONAL',e.target.value)} placeholder="Referencia..." />
                          </td>
                          <td style={{width:60}}>
                            <input type="number" style={{...inp,width:'100%'}} min={0} step=".01" value={p.VALOR} onChange={e=>setP(i,'VALOR',Number(e.target.value))} />
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <button className="btn btn-sm" style={{width:'100%',background:'var(--navy)',color:'#fff',border:'none',marginBottom:8}}
                  onClick={()=>setPagos(p=>[...p,{COD_FORMA_DE_PAGO:formasPago[0]?.COD||1,VALOR:total-totalPagos>0?Number((total-totalPagos).toFixed(2)):0,ADICIONAL:''}])}>
                  + Agregar forma de pago
                </button>
                <div className="sum-row2" style={{borderBottom:'none'}}>
                  <span>Total pagos</span>
                  <span style={{fontWeight:700,color:Math.abs(totalPagos-total)>0.01?'var(--red)':'var(--green)'}}>{fmt(totalPagos)}</span>
                </div>
              </div>

              {stockAfectado.length>0&&(
                <div className="side-card">
                  <h4>Stock afectado en esta factura</h4>
                  {stockAfectado.map(s=>{
                    const restante = s.inicial - s.usado;
                    return (
                      <div className="stock-afectado-row" key={s.nombre}>
                        <span>{s.nombre}</span>
                        <span className={`v ${restante<0?'warn':'ok'}`}>{s.inicial} → {restante}{restante<0?' ⚠':''}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {err&&<div style={{color:'var(--red)',fontSize:12,marginBottom:10,padding:'7px 9px',background:'#fff5f5',borderRadius:6}}>{err}</div>}
              {sriInfo&&(
                <div style={{fontSize:12,marginBottom:10,padding:'7px 9px',borderRadius:6,
                  background: sriInfo.ok ? '#f0fdf4' : '#fffbeb',
                  color:      sriInfo.ok ? '#15803d' : '#92400e',
                  border:     `1px solid ${sriInfo.ok ? '#86efac' : '#fcd34d'}`}}>
                  {sriInfo.msg}
                </div>
              )}

              <button className="btn btn-primary" style={{width:'100%',marginBottom:8}} disabled={saving||cajaAbierta===false} onClick={guardar}>
                {saving ? '⏳ Guardando y enviando al SRI...' : 'Guardar y facturar'}
              </button>
              <button className="btn" style={{width:'100%'}} onClick={onCerrar}>Cancelar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const inp = {padding:'3px 5px',fontSize:11,border:'1px solid var(--border)',borderRadius:3};

/* ── Detalle Factura ─────────────────────────────────── */
const SRI_ESTADOS = {
  F:{ label:'Pendiente SRI', bg:'#F59E0B', fg:'#fff' },
  A:{ label:'Autorizado',    bg:'#10B981', fg:'#fff' },
  D:{ label:'Devuelta',      bg:'#EF4444', fg:'#fff' },
  E:{ label:'Error SRI',     bg:'#6B7280', fg:'#fff' },
};

function DetalleFactura({ fac, onAnular, onNueva, onRefresh, onCerrarDetalle }) {
  const [sriLoading, setSriLoading] = useState(false);
  const [sriMsg,     setSriMsg]     = useState('');

  if (!fac) return (
    <div className="fac-panel">
      <div className="fac-hdr">Detalle</div>
      <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text3)',fontSize:13}}>
        Seleccione una factura
      </div>
      <div style={{padding:'10px 14px',borderTop:'1px solid var(--border)'}}>
        <button className="btn btn-primary btn-sm" style={{width:'100%'}} onClick={onNueva}>+ Nueva factura</button>
      </div>
    </div>
  );

  const est    = ESTADOS[fac.C_ESTADO]||ESTADOS.ACT;
  const sriEst = SRI_ESTADOS[fac.C_ESTADO_SRI] || SRI_ESTADOS.F;
  const subtotal = (fac.detalles||[]).filter(d=>d.C_ESTADO==='ACT').reduce((s,d)=>(Number(d.VALOR)||0)*(Number(d.CANTIDAD)||1)-(Number(d.DESCUENTO)||0)+s,0);
  const iva      = (fac.detalles||[]).filter(d=>d.C_ESTADO==='ACT').reduce((s,d)=>s+Number(d.IVA||0),0);
  const total    = subtotal+iva;

  const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const token = localStorage.getItem('token');

  const enviarSRI = async () => {
    if (!window.confirm('Enviar esta factura al SRI?')) return;
    setSriLoading(true); setSriMsg('');
    try {
      const { data } = await api.post(`/facturacion/${fac.COD}/enviar-sri`);
      if (data.estado === 'AUTORIZADO') {
        setSriMsg('✅ Factura AUTORIZADA — N° ' + data.numero_autorizacion);
      } else if (data.errores?.length) {
        setSriMsg('❌ ' + data.errores.join(' | '));
      } else if (data.mensajes?.length) {
        setSriMsg('⚠️ ' + data.mensajes.join(' | '));
      } else {
        setSriMsg('Estado: ' + data.estado + (data.detalle ? ' — ' + data.detalle : ''));
      }
      if (onRefresh) onRefresh(fac.COD);
    } catch(e) {
      setSriMsg('❌ ' + (e.response?.data?.error || e.message));
    }
    setSriLoading(false);
  };

  const descargarRIDE = () => {
    const url = `${API}/facturacion/${fac.COD}/ride`;
    fetch(url, { headers:{ Authorization:`Bearer ${token}` }})
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `RIDE_${fac.IDENTIFICACION}_${fac.COD}.pdf`;
        a.click();
      });
  };

  const consultarSRI = async () => {
    setSriLoading(true); setSriMsg('');
    try {
      const { data } = await api.post(`/facturacion/${fac.COD}/consultar-sri`);
      if (data.estado === 'AUTORIZADO') {
        setSriMsg('✅ Autorizada — N° ' + data.numero_autorizacion);
      } else {
        setSriMsg('Estado SRI: ' + data.estado + (data.mensajes?.length ? ' — ' + data.mensajes.join(' | ') : ''));
      }
      if (onRefresh) onRefresh(fac.COD);
    } catch(e) {
      setSriMsg('❌ ' + (e.response?.data?.error || e.message));
    }
    setSriLoading(false);
  };

  const generarNotaCredito = async () => {
    const motivo = window.prompt('Motivo de la nota de crédito (obligatorio):', '');
    if (!motivo?.trim()) return;
    if (!window.confirm(`Se emitirá una NOTA DE CRÉDITO real ante el SRI por el total de esta factura ($${fac.TOTAL||''}), y la factura quedará anulada automáticamente si se autoriza.\n\nMotivo: "${motivo.trim()}"\n\n¿Continuar?`)) return;
    setSriLoading(true); setSriMsg('');
    try {
      const { data } = await api.post(`/facturacion/${fac.COD}/nota-credito`, { motivo: motivo.trim() });
      if (data.estado === 'AUTORIZADO') {
        setSriMsg('✅ Nota de crédito AUTORIZADA — N° ' + data.numero_autorizacion);
      } else if (data.errores?.length) {
        setSriMsg('❌ ' + data.errores.join(' | '));
      } else if (data.mensajes?.length) {
        setSriMsg('⚠️ ' + data.mensajes.join(' | '));
      } else {
        setSriMsg('Estado: ' + data.estado + (data.detalle ? ' — ' + data.detalle : ''));
      }
      if (onRefresh) onRefresh(fac.COD);
    } catch(e) {
      setSriMsg('❌ ' + (e.response?.data?.error || e.message));
    }
    setSriLoading(false);
  };

  const descargarXML = () => {
    const url = `${API}/facturacion/${fac.COD}/xml`;
    fetch(url, { headers:{ Authorization:`Bearer ${token}` }})
      .then(r => r.text())
      .then(xml => {
        const a = document.createElement('a');
        a.href = 'data:application/xml,' + encodeURIComponent(xml);
        a.download = `${fac.IDENTIFICACION}_${fac.COD}.xml`;
        a.click();
      });
  };

  return (
    <div className="fac-panel">
      <div className="fac-hdr">
        <span>Factura #{fac.COD}</span>
        <div style={{display:'flex',gap:5,alignItems:'center'}}>
          <span style={{background:est.bg,color:est.fg,padding:'2px 7px',borderRadius:3,fontSize:10,fontWeight:600}}>{est.label}</span>
          <span style={{background:sriEst.bg,color:sriEst.fg,padding:'2px 7px',borderRadius:3,fontSize:10,fontWeight:600}}>{sriEst.label}</span>
          {onCerrarDetalle&&(
            <span style={{cursor:'pointer',fontSize:15,opacity:.85,marginLeft:4}} title="Cerrar detalle" onClick={onCerrarDetalle}>&#x2715;</span>
          )}
        </div>
      </div>
      <div className="fac-det-body">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
          {[['Paciente',fac.PACIENTE_NOMBRE||fac.RAZON_SOCIAL],['Identificacion',fac.IDENTIFICACION],
            ['Fecha',fac.FECHA_FMT||fac.FECHA],['Caja',fac.CAJA],
            ['Telefono',fac.TELEFONO],['Observacion',fac.OBSERVACION]].map(([l,v])=>(
            v&&<div key={l}>
              <div style={{fontSize:9,color:'var(--text3)',textTransform:'uppercase',fontWeight:700}}>{l}</div>
              <div style={{fontSize:11}}>{v}</div>
            </div>
          ))}
        </div>

        {/* Datos SRI si está autorizada */}
        {fac.C_ESTADO_SRI==='A'&&(
          <div style={{background:'#f0fdf4',border:'1px solid #86efac',borderRadius:6,padding:'8px 10px',marginBottom:10,fontSize:11}}>
            <div style={{fontWeight:700,color:'#15803d',marginBottom:4}}>✅ Autorizada por el SRI</div>
            <div style={{color:'#166534'}}><strong>N° Aut:</strong> {fac.AUTORIZACION_SRI}</div>
            <div style={{color:'#166534'}}><strong>Clave:</strong> <span style={{fontSize:9,wordBreak:'break-all'}}>{fac.CLAVE_ACCESO_SRI}</span></div>
          </div>
        )}

        {/* Mensaje de operación SRI */}
        {sriMsg&&(
          <div style={{background: sriMsg.startsWith('✅')?'#f0fdf4':'#fef2f2',
                       border:`1px solid ${sriMsg.startsWith('✅')?'#86efac':'#fca5a5'}`,
                       borderRadius:5,padding:'7px 10px',marginBottom:8,fontSize:11,
                       color: sriMsg.startsWith('✅')?'#15803d':'#dc2626'}}>
            {sriMsg}
          </div>
        )}

        <table className="fac-tbl">
          <thead><tr><th>Producto</th><th>Cant.</th><th>Precio</th><th>Total</th></tr></thead>
          <tbody>
            {(fac.detalles||[]).filter(d=>d.C_ESTADO==='ACT').map((d,i)=>(
              <tr key={i}>
                <td>{d.DESCRIPCION}</td>
                <td style={{textAlign:'center'}}>{d.CANTIDAD}</td>
                <td>{fmt(d.VALOR)}</td>
                <td style={{fontWeight:600}}>{fmt((Number(d.VALOR)||0)*(Number(d.CANTIDAD)||1)-(Number(d.DESCUENTO)||0))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="fac-total">
          <span>Subtotal</span><span style={{textAlign:'right'}}>{fmt(subtotal)}</span>
          <span>IVA</span><span style={{textAlign:'right'}}>{fmt(iva)}</span>
          <span style={{fontWeight:700,fontSize:14}}>TOTAL</span><span style={{textAlign:'right',fontWeight:700,fontSize:14}}>{fmt(total)}</span>
        </div>

        {(fac.pagos||[]).length>0&&(
          <div style={{marginTop:10}}>
            <div style={{fontSize:10,color:'var(--text3)',fontWeight:700,marginBottom:4}}>PAGOS</div>
            {(fac.pagos||[]).map((p,i)=>(
              <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:2}}>
                <span>{p.FORMA_DE_PAGO}</span><span style={{fontWeight:600}}>{fmt(p.VALOR)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Botones SRI */}
      <div style={{padding:'8px 14px',borderTop:'1px solid var(--border)',background:'#f8fafc'}}>
        <div style={{fontSize:9,color:'var(--text3)',fontWeight:700,marginBottom:5}}>FACTURACIÓN ELECTRÓNICA SRI</div>
        <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
          {fac.C_ESTADO==='ACT'&&fac.C_ESTADO_SRI!=='A'&&(
            <button className="btn btn-sm" disabled={sriLoading}
              style={{background:'#2563eb',color:'#fff',border:'none',fontSize:11}}
              onClick={enviarSRI}>
              {sriLoading?'Enviando...':' Enviar al SRI'}
            </button>
          )}
          {fac.CLAVE_ACCESO_SRI&&fac.C_ESTADO_SRI!=='A'&&(
            <button className="btn btn-sm" disabled={sriLoading}
              style={{background:'#0891b2',color:'#fff',border:'none',fontSize:11}}
              onClick={consultarSRI}>
              {sriLoading?'Consultando...':' Consultar SRI'}
            </button>
          )}
          <button className="btn btn-sm"
            style={{background:'#059669',color:'#fff',border:'none',fontSize:11}}
            onClick={descargarRIDE}>
            ⬇ RIDE PDF
          </button>
          {fac.C_ESTADO_SRI==='A'&&(
            <button className="btn btn-sm"
              style={{background:'#7c3aed',color:'#fff',border:'none',fontSize:11}}
              onClick={descargarXML}>
              ⬇ XML
            </button>
          )}
          {fac.C_ESTADO_SRI==='A'&&fac.C_ESTADO==='ACT'&&(
            <button className="btn btn-sm" disabled={sriLoading}
              style={{background:'#b91c1c',color:'#fff',border:'none',fontSize:11}}
              onClick={generarNotaCredito}>
              {sriLoading?'Procesando...':'🧾 Nota de Crédito'}
            </button>
          )}
        </div>
      </div>

      <div style={{padding:'8px 14px',borderTop:'1px solid var(--border)',display:'flex',gap:6}}>
        {fac.C_ESTADO==='ACT'&&(
          <button className="btn btn-sm" style={{background:'var(--red)',color:'#fff',border:'none'}} onClick={onAnular}>Anular</button>
        )}
        <button className="btn btn-sm btn-primary" style={{marginLeft:'auto'}} onClick={onNueva}>+ Nueva</button>
      </div>
    </div>
  );
}

/* ── Pagina Principal ────────────────────────────────── */
const DENOMINACIONES = [100, 50, 20, 10, 5, 1, 0.50, 0.25, 0.10, 0.05, 0.01];

function ModalCierreCaja({ cajas, onCerrar }) {
  const [codCaja, setCodCaja]   = useState(cajas[0]?.COD || '');
  const [estado, setEstado]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [fondoInicial, setFondoInicial] = useState('');
  const [egDesc, setEgDesc]     = useState('');
  const [egValor, setEgValor]   = useState('');
  const [denoms, setDenoms]     = useState(()=>DENOMINACIONES.reduce((o,d)=>({...o,[d]:0}),{}));
  const [cajero, setCajero]         = useState('');
  const [supervisor, setSupervisor] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [resultado, setResultado]   = useState(null);
  const [err, setErr]               = useState('');

  const [historial, setHistorial] = useState([]);

  const cargarEstado = useCallback(async (cod) => {
    if (!cod) return;
    setLoading(true); setErr('');
    try {
      const { data } = await api.get(`/facturacion/cierre-caja/estado?cod_caja=${cod}`);
      setEstado(data);
    } catch(e) { setErr(e.response?.data?.error || e.message); }
    setLoading(false);
  }, []);

  const cargarHistorial = useCallback(async (cod) => {
    if (!cod) return;
    try {
      const { data } = await api.get(`/facturacion/cierre-caja/historial?cod_caja=${cod}`);
      setHistorial(data || []);
    } catch { setHistorial([]); }
  }, []);

  useEffect(() => { cargarEstado(codCaja); cargarHistorial(codCaja); }, [codCaja, cargarEstado, cargarHistorial]);

  const abrir = async () => {
    setLoading(true); setErr('');
    try {
      await api.post('/facturacion/cierre-caja/abrir', { cod_caja: codCaja, fondo_inicial: Number(fondoInicial)||0 });
      setFondoInicial('');
      cargarEstado(codCaja);
    } catch(e) { setErr(e.response?.data?.error || e.message); }
    setLoading(false);
  };

  const agregarEgreso = async () => {
    if (!egDesc || !egValor) return;
    setErr('');
    try {
      await api.post('/facturacion/cierre-caja/egreso', {
        cod_turno_caja: estado.turno.COD, descripcion: egDesc, valor: Number(egValor)
      });
      setEgDesc(''); setEgValor('');
      cargarEstado(codCaja);
    } catch(e) { setErr(e.response?.data?.error || e.message); }
  };

  const totalVentas          = (estado?.ventas||[]).reduce((s,v)=>s+Number(v.TOTAL),0);
  const totalEfectivoVentas  = (estado?.ventas||[]).filter(v=>v.EFECTIVO==='V').reduce((s,v)=>s+Number(v.TOTAL),0);
  const totalEgresos         = (estado?.egresos||[]).reduce((s,e)=>s+Number(e.VALOR),0);
  const fondoInicialActual   = Number(estado?.turno?.FONDO_INICIAL || 0);
  const efectivoEsperado     = fondoInicialActual + totalEfectivoVentas - totalEgresos;
  const totalContado         = DENOMINACIONES.reduce((s,d)=>s + d*(Number(denoms[d])||0), 0);
  const diferencia           = totalContado - efectivoEsperado;
  const cuadra                = Math.abs(diferencia) < 0.01;

  const cerrar = async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await api.post('/facturacion/cierre-caja/cerrar', {
        cod_turno_caja: estado.turno.COD,
        denominaciones: DENOMINACIONES.map(d=>({denominacion:d, cantidad:Number(denoms[d])||0})),
        cajero, supervisor, observaciones,
      });
      setResultado(data);
      cargarHistorial(codCaja);
    } catch(e) { setErr(e.response?.data?.error || e.message); }
    setLoading(false);
  };

  return (
    <div>
      <div className="fpage-header">
        <div>
          <div className="crumb">Facturación → Cierre de caja</div>
          <h2>🔒 Cierre de caja</h2>
        </div>
        {!resultado&&(
          <select value={codCaja} disabled={estado?.abierto} onChange={e=>setCodCaja(e.target.value)}
            style={{fontSize:12,padding:'6px 8px',borderRadius:6,border:'none'}}>
            {cajas.map(c=><option key={c.COD} value={c.COD}>{c.CAJA}</option>)}
          </select>
        )}
      </div>
      <div className="fpage-body">
        {err&&<div style={{background:'var(--red-lt)',color:'var(--red)',padding:8,borderRadius:6,fontSize:12,marginBottom:10}}>{err}</div>}

        {resultado ? (
          <div style={{textAlign:'center',padding:20}}>
            <div style={{fontSize:32,marginBottom:8}}>✅</div>
            <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>Caja cerrada correctamente</div>
            <div style={{fontSize:12,color:'var(--text3)',marginBottom:16}}>
              Diferencia: <strong style={{color:Math.abs(resultado.diferencia)<0.01?'var(--green)':'var(--red)'}}>
                {fmt(resultado.diferencia)}
              </strong>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'center'}}>
              <button className="btn btn-primary" onClick={async()=>{
                try{
                  const token=localStorage.getItem('token');
                  const res=await fetch(`${process.env.REACT_APP_API_URL||'http://localhost:3001/api'}/facturacion/cierre-caja/${resultado.cod}/pdf`,
                    {headers:{Authorization:`Bearer ${token}`}});
                  if(!res.ok){alert('Error al generar el PDF');return;}
                  const blob=await res.blob();
                  const a=document.createElement('a');
                  a.href=URL.createObjectURL(blob);
                  a.download=`Cierre_Caja_${resultado.cod}.pdf`;
                  a.click();
                }catch(e){alert('Error: '+e.message);}
              }}>📄 Descargar PDF</button>
              <button className="btn btn-sm" onClick={onCerrar}>← Volver al listado</button>
            </div>
          </div>
        ) : loading&&!estado ? (
          <div style={{textAlign:'center',padding:24,color:'var(--text3)'}}>Cargando...</div>
        ) : !estado?.abierto ? (
          <div className="flt-card" style={{maxWidth:360}}>
            <h4>Abrir turno de caja</h4>
            <div style={{fontSize:12,color:'var(--text3)',marginBottom:12}}>
              Esta caja no tiene un turno abierto. Registra el fondo inicial para abrirla.
            </div>
            <label className="form-label" style={{fontSize:11}}>Fondo inicial</label>
            <input type="number" step="0.01" className="form-control" value={fondoInicial}
              onChange={e=>setFondoInicial(e.target.value)} placeholder="0.00" style={{marginBottom:12}}/>
            <button className="btn btn-primary" disabled={loading} onClick={abrir}>
              {loading?'Abriendo...':'Abrir caja'}
            </button>
          </div>
        ) : (
          <>
            <div style={{fontSize:11.5,color:'var(--text3)',marginBottom:14}}>
              Turno abierto: {new Date(estado.turno.FECHA_APERTURA).toLocaleString('es-EC')}
            </div>

            <div className="resumen-grid2">
              <div className="resumen-card2"><div className="lbl">Fondo inicial</div><div className="num">{fmt(fondoInicialActual)}</div></div>
              <div className="resumen-card2"><div className="lbl">Ventas del turno</div><div className="num">{fmt(totalVentas)}</div></div>
              <div className="resumen-card2"><div className="lbl">Efectivo esperado</div><div className="num">{fmt(efectivoEsperado)}</div></div>
              <div className="resumen-card2"><div className="lbl">Diferencia</div><div className={`num ${cuadra?'ok':'bad'}`}>{fmt(diferencia)}</div></div>
            </div>

            <div className="flt-card">
              <h4>Ventas por forma de pago</h4>
              {(estado.ventas||[]).length===0
                ? <div style={{fontSize:11.5,color:'var(--text3)'}}>Sin ventas en este turno</div>
                : (
                  <div style={{overflowX:'auto'}}>
                  <table className="formas-tbl2">
                    <thead><tr><th>Forma de pago</th><th className="num">Total</th></tr></thead>
                    <tbody>
                      {(estado.ventas||[]).map(v=>(
                        <tr key={v.COD_FP||v.FORMA_DE_PAGO}>
                          <td>{iconoFormaPago(v.FORMA_DE_PAGO)} {v.FORMA_DE_PAGO||'Sin especificar'}</td>
                          <td className="num">{fmt(v.TOTAL)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
            </div>

            <div className="flt-card">
              <h4>Egresos del turno</h4>
              {(estado.egresos||[]).map(e=>(
                <div key={e.COD} style={{display:'flex',justifyContent:'space-between',fontSize:12.5,padding:'4px 0'}}>
                  <span>{e.DESCRIPCION}</span>
                  <span style={{fontWeight:600,color:'var(--red)'}}>-{fmt(e.VALOR)}</span>
                </div>
              ))}
              {(estado.egresos||[]).length===0&&<div style={{fontSize:11.5,color:'var(--text3)',marginBottom:6}}>Sin egresos registrados</div>}
              <div style={{display:'flex',gap:6,marginTop:8}}>
                <input className="form-control" style={{fontSize:12,flex:2}} placeholder="Descripción del gasto"
                  value={egDesc} onChange={e=>setEgDesc(e.target.value)}/>
                <input type="number" step="0.01" className="form-control" style={{fontSize:12,flex:1}} placeholder="$0.00"
                  value={egValor} onChange={e=>setEgValor(e.target.value)}/>
                <button className="btn btn-sm" onClick={agregarEgreso}>+ Agregar</button>
              </div>
            </div>

            <div className="flt-card">
              <h4>Conteo de efectivo por denominación</h4>
              <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:8,marginBottom:10}}>
                {DENOMINACIONES.map(d=>(
                  <div key={d}>
                    <div style={{fontSize:10,color:'var(--text3)',marginBottom:3}}>💵 {fmt(d)}</div>
                    <input type="number" min="0" className="form-control" style={{fontSize:12,textAlign:'center'}}
                      value={denoms[d]} onChange={e=>setDenoms(s=>({...s,[d]:e.target.value}))}/>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:12.5,padding:'6px 0',borderTop:'1px solid var(--border)'}}>
                <span style={{fontWeight:700}}>Total contado</span><span style={{fontWeight:700}}>{fmt(totalContado)}</span>
              </div>
            </div>

            <div className={`diff-banner2 ${cuadra?'ok':'bad'}`}>
              {cuadra?'✅':'⚠️'} <div>{cuadra
                ? <b>La caja cuadra — el efectivo contado coincide con lo esperado.</b>
                : <><b>Hay una diferencia de {fmt(Math.abs(diferencia))}</b> ({diferencia>0?'sobrante':'faltante'}). Puedes cerrar igual dejando un motivo en observaciones, o volver a contar antes de cerrar.</>}
              </div>
            </div>

            <div className="flt-row" style={{marginBottom:10}}>
              <div className="flt-g">
                <label>Cajero responsable</label>
                <input className="form-control" value={cajero} onChange={e=>setCajero(e.target.value)}/>
              </div>
              <div className="flt-g">
                <label>Supervisor</label>
                <input className="form-control" value={supervisor} onChange={e=>setSupervisor(e.target.value)}/>
              </div>
            </div>
            <div style={{marginBottom:16}}>
              <label className="form-label" style={{fontSize:10}}>Observaciones</label>
              <textarea className="form-control" rows={2} style={{fontSize:12.5}} value={observaciones} onChange={e=>setObservaciones(e.target.value)}/>
            </div>

            <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginBottom:24}}>
              <button className="btn btn-sm" onClick={onCerrar}>Cancelar</button>
              <button className="btn btn-primary" disabled={loading} onClick={cerrar}>
                {loading?'Cerrando...':'🔒 Confirmar cierre'}
              </button>
            </div>
          </>
        )}

        <div className="flt-card" style={{marginBottom:0}}>
          <h4>Cierres anteriores</h4>
          {historial.length===0
            ? <div style={{fontSize:11.5,color:'var(--text3)'}}>Sin cierres registrados todavía para esta caja</div>
            : (
              <div style={{overflowX:'auto'}}>
              <table className="hist-tbl2">
                <thead><tr><th>Fecha</th><th>Cajero</th><th>Total</th><th>Diferencia</th><th></th></tr></thead>
                <tbody>
                  {historial.map(h=>(
                    <tr key={h.COD}>
                      <td>{h.FECHA_CIERRE?new Date(h.FECHA_CIERRE).toLocaleString('es-EC'):'—'}</td>
                      <td>{h.CAJERO||'—'}</td>
                      <td>{fmt(h.TOTAL_SISTEMA)}</td>
                      <td style={{color:Math.abs(h.DIFERENCIA)<0.01?'var(--green)':'var(--red)',fontWeight:700}}>
                        {fmt(h.DIFERENCIA)}{Math.abs(h.DIFERENCIA)<0.01?'':(h.DIFERENCIA>0?' (sobrante)':' (faltante)')}
                      </td>
                      <td>
                        <span className="lnk2" onClick={async()=>{
                          try{
                            const token=localStorage.getItem('token');
                            const res=await fetch(`${process.env.REACT_APP_API_URL||'http://localhost:3001/api'}/facturacion/cierre-caja/${h.COD}/pdf`,
                              {headers:{Authorization:`Bearer ${token}`}});
                            if(!res.ok){alert('Error al generar el PDF');return;}
                            const blob=await res.blob();
                            const a=document.createElement('a');
                            a.href=URL.createObjectURL(blob);
                            a.download=`Cierre_Caja_${h.COD}.pdf`;
                            a.click();
                          }catch(e){alert('Error: '+e.message);}
                        }}>📄 PDF</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default function Facturacion() {
  const [searchParams] = useSearchParams();
  const hoy = (()=>{const d=new Date();return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;})();

  const [moduloTab,  setModuloTab]  = useState('listado'); // 'listado' | 'nueva' | 'cierre'
  const [filtros,    setFiltros]    = useState({desde:hoy,hasta:hoy,estado:'',q:''});
  const [sugsListado,setSugsListado]= useState([]);
  const [stats,      setStats]      = useState(null);
  const [facturas,   setFacturas]   = useState([]);
  const [facSel,     setFacSel]     = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [turnoUrl,   setTurnoUrl]   = useState(null); // paciente cargado desde URL
  const [cajas,      setCajas]      = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [servicios,  setServicios]  = useState([]);
  const [ivaGlobal,  setIvaGlobal]  = useState(15);
  const [page,       setPage]       = useState(1);
  const [total,      setTotal]      = useState(0);

  const cargar = useCallback(async()=>{
    setLoading(true);
    try{
      const p = new URLSearchParams({desde:filtros.desde,hasta:filtros.hasta,estado:filtros.estado,q:filtros.q,page,limit:30}).toString();
      const{data}=await api.get(`/facturacion?${p}`);
      setFacturas(data.data); setTotal(data.total);
    }catch(e){console.error(e);}
    finally{setLoading(false);}
  },[filtros,page]);

  const cargarStats = useCallback(async()=>{
    try{ const{data}=await api.get('/facturacion/stats'); setStats(data); }catch{}
  },[]);

  const cargarDet = async cod=>{
    try{const{data}=await api.get(`/facturacion/${cod}`);setFacSel(data);}catch{}
  };

  useEffect(()=>{cargar();},[cargar]);
  useEffect(()=>{cargarStats();},[cargarStats]);

  // Autocompletar paciente en el buscador del listado
  useEffect(()=>{
    if (filtros.q.length < 2) { setSugsListado([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/pacientes?q=${encodeURIComponent(filtros.q)}&limit=6`); setSugsListado(data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [filtros.q]);

  const exportarExcel = () => {
    const filas = [['Fecha','Paciente','Identificación','Caja','Total','Estado']];
    facturas.forEach(f=>{
      const est=ESTADOS[f.C_ESTADO]||ESTADOS.ACT;
      filas.push([f.FECHA, f.RAZON_SOCIAL, f.IDENTIFICACION, f.CAJA, Number(f.TOTAL||0).toFixed(2), est.label]);
    });
    const csv = filas.map(r=>r.map(c=>`"${String(c??'').replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿'+csv], {type:'text/csv;charset=utf-8;'});
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `Facturas_${filtros.desde}_${filtros.hasta}.csv`;
    a.click();
  };

  // Auto-abrir "Nueva factura" con paciente si viene de ?paciente=COD
  useEffect(()=>{
    const codPac   = searchParams.get('paciente');
    const codTurno = searchParams.get('turno');
    if (!codPac) return;

    if (codTurno) {
      // Cargar datos completos del turno (incluye servicio + precio)
      api.get(`/facturacion/turno/${codTurno}`).then(r => {
        const t = r.data;
        if (!t?.COD_PACIENTE) return;
        setTurnoUrl(t);
        setModuloTab('nueva');
      }).catch(()=>{
        // Fallback: solo datos del paciente
        api.get(`/pacientes/${codPac}`).then(r => {
          const p = r.data;
          // OJO: /pacientes/:id devuelve p.* tal cual está en la tabla — NO
          // existe una columna DIRECCION, la dirección real vive en
          // CALLE_PRINCIPAL o, si esa no está, en DIRECCION_DOMICILIO
          // (mismo criterio que ya usa el backend en /facturacion/turno/:id).
          // Leer p.DIRECCION a secas siempre daba undefined → '' y por eso
          // la factura salía sin dirección al entrar por acá.
          const direccionPaciente = p.CALLE_PRINCIPAL || p.DIRECCION_DOMICILIO || '';
          if (p?.COD) { setTurnoUrl({ COD_PACIENTE:p.COD, IDENTIFICACION:p.IDENTIFICACION,
            APELLIDO_PATERNO:p.APELLIDO_PATERNO||'', APELLIDO_MATERNO:p.APELLIDO_MATERNO||'',
            PRIMER_NOMBRE:p.PRIMER_NOMBRE||'', TELEFONO:p.TELEFONO||'',
            EMAIL:p.EMAIL||'', DIRECCION:direccionPaciente, NUMERO:codTurno }); setModuloTab('nueva'); }
        }).catch(()=>{});
      });
    } else {
      api.get(`/pacientes/${codPac}`).then(r => {
        const p = r.data;
        if (!p?.COD) return;
        // Mismo fallback de dirección que arriba — ver comentario.
        const direccionPaciente = p.CALLE_PRINCIPAL || p.DIRECCION_DOMICILIO || '';
        setTurnoUrl({ COD_PACIENTE:p.COD, C_TIPO_IDENTIFICACION:p.C_TIPO_IDENTIFICACION||'CDL',
          IDENTIFICACION:p.IDENTIFICACION, APELLIDO_PATERNO:p.APELLIDO_PATERNO||'',
          APELLIDO_MATERNO:p.APELLIDO_MATERNO||'', PRIMER_NOMBRE:p.PRIMER_NOMBRE||'',
          TELEFONO:p.TELEFONO||'', EMAIL:p.EMAIL||'', DIRECCION:direccionPaciente });
        setModuloTab('nueva');
      }).catch(()=>{});
    }
  }, [searchParams]);
  useEffect(()=>{
    api.get('/facturacion/catalogos/cajas').then(r=>setCajas(r.data)).catch(()=>{});
    api.get('/facturacion/catalogos/formas-pago').then(r=>setFormasPago(r.data)).catch(()=>{});
    api.get('/catalogos/servicios').then(r=>setServicios(r.data)).catch(()=>{});
    api.get('/configuracion/iva').then(r=>setIvaGlobal(r.data?.iva||15)).catch(()=>{});
  },[]);

  const anular = async()=>{
    const msg = facSel?.C_ESTADO_SRI === 'A'
      ? 'Esta factura YA FUE AUTORIZADA por el SRI — es un documento fiscal válido.\n\nAnularla aquí solo la marca como anulada en este sistema, NO la reversa ante el SRI. Para eso corresponde emitir una NOTA DE CRÉDITO por separado (este sistema todavía no la genera automáticamente).\n\n¿Continuar de todas formas?'
      : '¿Anular esta factura? Como no fue autorizada por el SRI, no es un documento fiscal válido — se puede anular sin necesidad de nota de crédito.';
    if(!window.confirm(msg))return;
    await api.patch(`/facturacion/${facSel.COD}/anular`, { motivo: 'Anulado por usuario' });
    cargar(); cargarStats(); setFacSel(f=>({...f,C_ESTADO:'ANL'}));
  };

  const irNueva = () => { setFacSel(null); setModuloTab('nueva'); };

  return (
    <>
      <style>{css}</style>
      <div className="fade-up">
        <div className="fmod-tabs">
          <button className={`fmod-tab ${moduloTab==='nueva'?'on':''}`} onClick={irNueva}>+ Nueva factura</button>
          <button className={`fmod-tab ${moduloTab==='listado'?'on':''}`} onClick={()=>setModuloTab('listado')}>Listado de facturas</button>
          <button className={`fmod-tab ${moduloTab==='cierre'?'on':''}`} onClick={()=>setModuloTab('cierre')}>🔒 Cierre de caja</button>
        </div>

        {moduloTab==='listado' && (
          <div>
            {stats&&(
              <div className="kpi-grid">
                <div className="kpi-card">
                  <div className="lbl">Facturado hoy</div>
                  <div className="num">{fmt(stats.facturadoHoy)}</div>
                  <span className="tag g">{stats.facturasHoyCount} facturas</span>
                </div>
                <div className="kpi-card">
                  <div className="lbl">Facturas del mes</div>
                  <div className="num">{stats.facturasMes}</div>
                  <span className="tag b">{new Date().toLocaleDateString('es-EC',{month:'long',year:'numeric'})}</span>
                </div>
                <div className="kpi-card">
                  <div className="lbl">Pendientes de facturar</div>
                  <div className="num">{stats.pendientes}</div>
                  <span className="tag a">cargos sin facturar aún</span>
                </div>
                <div className="kpi-card">
                  <div className="lbl">Anuladas este mes</div>
                  <div className="num">{stats.anuladasMes}</div>
                  <span className="tag r">revisar motivo</span>
                </div>
              </div>
            )}

            <div className="flt-card">
              <h4>
                Buscar y filtrar
                <span className="lnk2" style={{cursor:'pointer'}} onClick={exportarExcel}>Exportar a Excel ⬇</span>
              </h4>
              <div className="flt-row">
                <div className="flt-g">
                  <label>Paciente</label>
                  <input placeholder="Buscar por paciente (nombre o cédula)..." value={filtros.q}
                    onChange={e=>{setFiltros(f=>({...f,q:e.target.value}));setPage(1);}} />
                  {sugsListado.length>0&&(
                    <div className="flt-auto">
                      {sugsListado.map(p=>(
                        <div key={p.COD} className="flt-auto-item"
                          onClick={()=>{setFiltros(f=>({...f,q:p.NOMBRE_COMPLETO?.trim()||''}));setSugsListado([]);setPage(1);}}>
                          <b>{p.NOMBRE_COMPLETO?.trim()}</b><span>{p.IDENTIFICACION}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flt-g sm">
                  <label>Desde</label>
                  <input type="date" value={filtros.desde} onChange={e=>{setFiltros(f=>({...f,desde:e.target.value}));setPage(1);}} />
                </div>
                <div className="flt-g sm">
                  <label>Hasta</label>
                  <input type="date" value={filtros.hasta} onChange={e=>{setFiltros(f=>({...f,hasta:e.target.value}));setPage(1);}} />
                </div>
                <div className="flt-g sm">
                  <label>Estado</label>
                  <select value={filtros.estado} onChange={e=>{setFiltros(f=>({...f,estado:e.target.value}));setPage(1);}}>
                    <option value="">Todos los estados</option>
                    <option value="ACT">Activa</option>
                    <option value="ANL">Anulada</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flt-card" style={{background:'#fff'}}>
              {loading&&<div style={{textAlign:'center',padding:'20px 0',color:'var(--text3)'}}>Cargando...</div>}
              {!loading&&facturas.length===0&&(
                <div style={{textAlign:'center',padding:'28px 0',color:'var(--text3)'}}>Sin facturas para los filtros seleccionados</div>
              )}
              {!loading&&facturas.length>0&&(
                <div style={{overflowX:'auto'}}>
                  <table className="lst-tbl">
                    <thead>
                      <tr><th>Fecha</th><th>Paciente</th><th>Caja</th><th className="num">Total</th><th>Estado</th><th>SRI</th><th>Acciones</th></tr>
                    </thead>
                    <tbody>
                      {facturas.map(f=>{
                        const est=ESTADOS[f.C_ESTADO]||ESTADOS.ACT;
                        const estSri=ESTADOS_SRI[f.C_ESTADO_SRI]||ESTADOS_SRI.F;
                        return (
                          <tr key={f.COD} onClick={()=>cargarDet(f.COD)}>
                            <td>{f.FECHA}</td>
                            <td>#{f.COD} — {f.RAZON_SOCIAL}</td>
                            <td>{f.CAJA}</td>
                            <td className="num">{fmt(f.TOTAL)}</td>
                            <td><span className={`pill2 ${f.C_ESTADO==='ANL'?'anulada':'activa'}`}>{est.label}</span></td>
                            <td><span className={`pill2 ${estSri.clase}`}>{estSri.label}</span></td>
                            <td onClick={e=>e.stopPropagation()}>
                              <span className="lnk2" onClick={()=>cargarDet(f.COD)}>Ver</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="tbl-foot2">
                <span>Mostrando {facturas.length===0?0:(page-1)*30+1}–{(page-1)*30+facturas.length} de {total}</span>
                <div className="pager2">
                  <button className="pg2" disabled={page===1} onClick={()=>setPage(p=>p-1)}>&#8249;</button>
                  <span className="pg2 cur">{page}</span>
                  <button className="pg2" disabled={page*30>=total} onClick={()=>setPage(p=>p+1)}>&#8250;</button>
                </div>
              </div>
            </div>

            {facSel&&(
              <div style={{marginTop:16,maxWidth:640}}>
                <DetalleFactura fac={facSel} onAnular={anular} onNueva={irNueva} onRefresh={cargarDet} onCerrarDetalle={()=>setFacSel(null)} />
              </div>
            )}
          </div>
        )}

        {moduloTab==='nueva' && (
          <ModalFactura
            turno={turnoUrl}
            cajas={cajas} formasPago={formasPago} servicios={servicios}
            ivaGlobal={ivaGlobal}
            onGuardar={()=>{setModuloTab('listado');setTurnoUrl(null);cargar();cargarStats();}}
            onCerrar={()=>{setModuloTab('listado');setTurnoUrl(null);}}
          />
        )}

        {moduloTab==='cierre' && (
          <ModalCierreCaja cajas={cajas} onCerrar={()=>setModuloTab('listado')} />
        )}
      </div>
    </>
  );
}
